// dtmPlot.ts — a hexagonal plot of measured Martian ground, as a solid object.
//
// Two departures from what the Atlas used to draw:
//
//   1. The hexagon IS the terrain. Not a hairline hovering over a square plane —
//      a hex-shaped block of land with cut walls, which is the thing the player
//      is eventually going to own.
//   2. The light and the land are the same argument. The shadow crawling across
//      a plot at dawn is the same horizon march that decides the plot's
//      insolation, which is the same number that marks it down in the Sampler.
//
// The mesh is a triangular lattice in axial coordinates, so the boundary is
// exactly the hexagon — no clipping a square grid and stitching the ragged edge.

import * as THREE from 'three';
import type { AtmosphereHandle } from './atmosphere';
import {
  PlotField, bilinearSample, insideHexZ, hexEdgeDepth, bandFor, MARCH_START_LIFT_M,
  type SubPlot, type DataTier, type PlotClimate, type PlotMeta,
} from './plotField.ts';
import { metresPerPixel } from './camera.ts';
import { GROUND_SPECULAR_GLSL } from './groundBrdf.ts';
import { buildLatticeBuffers, type LatticeBuffers, type LatticePlace } from './plotLattice.ts';
import { createRockField, ROCK_DEFAULTS, ROCK_MIN_PX, type RockField, type RockLaw,
  type RockPush, type RockTuning } from './rockField.ts';
import { Rover } from './rover.ts';
import {
  createStructureMeshes, type StructureEntry, type StructureLook, type StructureMeshes,
} from './structureMeshes.ts';
import type { HaulPoint, HaulRoute } from './structureHauls';
import { devFlag, urlFlag, TEST_SWITCHES } from '../urlFlags.ts';
import type { SubPlotMarkMode } from './subPlotMark';
import { SEA_GLSL } from './seaSurface.ts';

const SQRT3 = Math.sqrt(3);
const _invWorld = new THREE.Matrix4();
const _liftV = new THREE.Vector3();
const _push: RockPush = { x: 0, z: 0, depth: 0 };

/**
 * Hex lattice, evaluated rather than rasterised.
 *
 * Boundaries used to be painted into the overlay texture, which is one fixed
 * resolution across the whole claim — 22 m a texel on Jezero. Zoomed in to
 * 1.65 km that is twenty screen pixels per texel, and the property lines turn
 * into soft ribbons. An SDF has no resolution, so the same line is one pixel
 * wide at every distance.
 *
 * Orientation is pointy-along-Z, matching insideHexZ and hexCorners:
 * vertices at (0, ±R) and (±R√3/2, ±R/2).
 */
export const HEX_GRID_GLSL = /* glsl */ `
const float SQ3 = 1.7320508;

/** Signed distance to a hexagon's edge, negative inside. In metres. */
float hexSDF(vec2 p, float R){
  vec2 a = abs(p);
  return max(a.x - R * SQ3 * 0.5, (a.x + SQ3 * a.y - SQ3 * R) * 0.5);
}

/** Axial (q, r) of the lattice cell containing p, by cube rounding. */
vec2 hexAxial(vec2 p, float R){
  float r = p.y / (1.5 * R);
  float q = p.x / (R * SQ3) - r * 0.5;
  float cx = q, cz = r, cy = -q - r;
  float rx = floor(cx + 0.5), ry = floor(cy + 0.5), rz = floor(cz + 0.5);
  float dx = abs(rx - cx), dy = abs(ry - cy), dz = abs(rz - cz);
  if (dx > dy && dx > dz) rx = -ry - rz;
  else if (dy > dz) ry = -rx - rz;
  else rz = -rx - ry;
  return vec2(rx, rz);
}

vec2 hexCentreOf(vec2 qr, float R){
  return vec2((qr.x + qr.y * 0.5) * R * SQ3, qr.y * 1.5 * R);
}

/** Centre of the lattice cell containing p, by cube rounding. */
vec2 hexCentre(vec2 p, float R){
  float r = p.y / (1.5 * R);
  float q = p.x / (R * SQ3) - r * 0.5;
  float cx = q, cz = r, cy = -q - r;
  float rx = floor(cx + 0.5), ry = floor(cy + 0.5), rz = floor(cz + 0.5);
  float dx = abs(rx - cx), dy = abs(ry - cy), dz = abs(rz - cz);
  // Whichever axis rounded furthest is the one recomputed, so x+y+z stays 0.
  if (dx > dy && dx > dz) rx = -ry - rz;
  else if (dy > dz) ry = -rx - rz;
  else rz = -rx - ry;
  return vec2((rx + rz * 0.5) * R * SQ3, rz * 1.5 * R);
}

/** A line of constant SCREEN width along the |sdf| = 0 contour. */
float hexLine(float sdf, float widthPx, vec2 wp){
  float w = fwidth(wp.x) + fwidth(wp.y);
  return 1.0 - smoothstep(0.0, w * widthPx, abs(sdf));
}
`;


/**
 * Declares the atmosphere uniforms the plot's own materials need beyond
 * `atmosphere.ts`'s own chunk (`uAtmoSunDir`, `uAtmoViewHeight` and the LUT
 * samplers, already merged from `atmo.uniforms`): the exposure scalar that
 * turns the LUT's per-unit-illuminance in-scatter into renderer units, the
 * scale the tiles put their own sunlight on to meet it (`uAtmoGroundScale`,
 * src/atlas/atmosphereView.ts), the negative-control switch, `descent-dojo --inject aerial-tail-on-ground`'s
 * own switch back to the sphere gate, and the deck's own diffuse radiance the
 * haze converges on as it thickens. All are set once a frame from
 * src/atlas.ts and shared with the tile material's own `sharedUniforms`, so
 * one flag turns the aerial term off, or its tail's ground gate back on,
 * everywhere terrain is drawn.
 */
const ATMO_PLOT_DECL_GLSL = /* glsl */ `
uniform float uAtmoSunIrradiance;
uniform float uAtmoGroundScale;
uniform float uAtmoAerialOff;
uniform float uAtmoAerialTailOnGround;
uniform vec3 uAtmoDeckSkyLight;
`;

/**
 * Cascaded shadow sampling for the rover, rocks and structures - the things
 * the heightfield's own march (above) cannot see because they are not in it.
 *
 * Three lights and three depth maps, picked by the fragment's own distance
 * from the walkaround camera (`uCsmSplits`, world units). Depth is packed
 * into RGBA the way three.js always packs a shadow map regardless of
 * `shadowMap.type` - the PCF softening is three's own multi-tap sampling, not
 * a different texture format - so `#include <packing>` reads it with no
 * chunk of our own. `uCsmOn` is zero at every band but SURFACE and under the
 * `setShadowDebug({ csm: false })` negative control, which collapses every
 * tap below to fully lit - the state this tree was in before any caster
 * existed.
 */
/** `?nostructmesh=1`: the pads alone, as before the meshes - the control, and the frame cost's baseline. */
const structureMeshesOff = devFlag(() => TEST_SWITCHES && urlFlag('nostructmesh'));

export const PLOT_CSM_GLSL = /* glsl */ `
#include <packing>
uniform sampler2D uCsmMap0, uCsmMap1, uCsmMap2;
uniform mat4 uCsmMatrix0, uCsmMatrix1, uCsmMatrix2;
uniform vec2 uCsmSplits;
// The ground point the boxes are centred on, not the eye - see uCsmAnchor's
// own comment in src/atlas/cascades.ts.
uniform vec3 uCsmAnchor;
uniform float uCsmOn;
/** One texel of a cascade's depth map: CSM_MAP_PX in src/atlas/cascades.ts owns the
 *  size, and the PCF spacing below is one texel of it. */
uniform float uCsmTexel;

float csmTap(sampler2D shadowMap, vec3 coord) {
  float d = unpackRGBAToDepth(texture2D(shadowMap, coord.xy));
  return (coord.z - 0.0015 > d) ? 0.0 : 1.0;
}

float csmSample(sampler2D shadowMap, mat4 m, vec3 worldPos) {
  vec3 coord = (m * vec4(worldPos, 1.0)).xyz;
  if (coord.x < 0.0 || coord.x > 1.0 || coord.y < 0.0 || coord.y > 1.0
      || coord.z < 0.0 || coord.z > 1.0) return 1.0;
  float vis = 0.0;
  for (int dx = -1; dx <= 1; dx++) {
    for (int dy = -1; dy <= 1; dy++) {
      vis += csmTap(shadowMap, coord + vec3(float(dx), float(dy), 0.0) * uCsmTexel);
    }
  }
  return vis / 9.0;
}

// worldPos and anchorDist share the frame cameraPosition is already in, and
// the distance is to uCsmAnchor - the ground point the boxes are centred on -
// and not to the eye, so the split matches the box a fragment lands in.
float csmVisibility(vec3 worldPos, float anchorDist) {
  if (uCsmOn < 0.5) return 1.0;
  if (anchorDist < uCsmSplits.x) return csmSample(uCsmMap0, uCsmMatrix0, worldPos);
  if (anchorDist < uCsmSplits.y) return csmSample(uCsmMap1, uCsmMatrix1, worldPos);
  return csmSample(uCsmMap2, uCsmMatrix2, worldPos);
}
`;

// ─────────────────────────────────────────────────────────────────────────────
// Geometry
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The plot's surface lattice as a geometry.
 *
 * The arithmetic is `plotLattice.ts`'s, which the tile worker calls too; this
 * wraps the two buffers with no copy, whichever thread built them.
 */
function hexLattice(buffers: LatticeBuffers, R: number): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(buffers.position, 3));
  g.setIndex(new THREE.BufferAttribute(buffers.index, 1));
  // Displacement happens in the vertex shader, so three cannot compute a useful
  // bounding volume from these flat positions. Give it one big enough to never
  // cull the plot out from under us.
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), R * 4);
  return g;
}

/** The six corners of a flat-top hexagon, in order, starting due north. */
export function hexCorners(R: number): Array<[number, number]> {
  return [
    [0, -R],
    [(R * SQRT3) / 2, -R / 2],
    [(R * SQRT3) / 2, R / 2],
    [0, R],
    [(-R * SQRT3) / 2, R / 2],
    [(-R * SQRT3) / 2, -R / 2],
  ];
}

/**
 * Side walls: the hex boundary dropped to a base plane. Vertex Y for the top
 * ring is displaced by the same shader as the surface, so the seam is exact.
 */
// Which parent-neighbour each hex edge faces. Edge e runs corners[e] ->
// corners[e+1]; the midpoints work out to NE, E, SE, SW, W, NW in that order.
const EDGE_NEIGHBOUR: Array<[number, number]> = [
  [1, -1],
  [1, 0],
  [0, 1],
  [-1, 1],
  [-1, 0],
  [0, -1],
];

function hexWalls(
  R: number,
  perEdge: number,
  depth: number,
  places: Array<{ q: number; r: number; x_m: number; z_m: number }>,
): THREE.BufferGeometry {
  const corners = hexCorners(R);
  const pos: number[] = [];
  const nrm: number[] = [];
  const isTop: number[] = [];
  const idx: number[] = [];
  let n = 0;
  const present = new Set(places.map((p) => `${p.q},${p.r}`));

  for (const place of places) {
  for (let e = 0; e < 6; e++) {
    // Skip any face that abuts another plot. Interior walls would slice the
    // cluster into separate blocks; the whole point of adjoining plots is that
    // the ground between them is ground.
    const nb = EDGE_NEIGHBOUR[e]!;
    if (present.has(`${place.q + nb[0]},${place.r + nb[1]}`)) continue;
    const [ax0, az0] = corners[e]!;
    const [bx0, bz0] = corners[(e + 1) % 6]!;
    const ax = ax0 + place.x_m;
    const az = az0 + place.z_m;
    const bx = bx0 + place.x_m;
    const bz = bz0 + place.z_m;
    // Outward horizontal normal for this face. Without it every wall takes the
    // same constant shade, three of the six end up brighter than the sunlit
    // ground, and the block stops reading as a solid object.
    const ex = bx - ax;
    const ez = bz - az;
    const el = Math.hypot(ex, ez) || 1;
    const nx = ez / el;
    const nz = -ex / el;
    for (let i = 0; i <= perEdge; i++) {
      const t = i / perEdge;
      const x = ax + (bx - ax) * t;
      const z = az + (bz - az) * t;
      pos.push(x, 0, z);
      nrm.push(nx, 0, nz);
      isTop.push(1);
      pos.push(x, -depth, z);
      nrm.push(nx, 0, nz);
      isTop.push(0);
      if (i < perEdge) {
        const o = n + i * 2;
        idx.push(o, o + 1, o + 2, o + 2, o + 1, o + 3);
      }
    }
    n += (perEdge + 1) * 2;
  }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute('isTop', new THREE.Float32BufferAttribute(isTop, 1));
  g.setIndex(idx);
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), R * 4);
  return g;
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared GLSL — one height function, exactly as the CPU samples it
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Regolith — the ground below the resolution of the photograph
// ─────────────────────────────────────────────────────────────────────────────
//
// The orthoimage is 12 m/px at this tier and 25 cm/px at its very best. Below
// that texel there is nothing, so from a rover's eye the ground has always been
// a flat wash of colour with boulders standing on it. That is the gap: not the
// colour, which is measured and correct, but the ABSENCE OF SURFACE.
//
// ── Why this is minted and not draped ───────────────────────────────────────
//
// The obvious move is to buy or borrow a ground texture. Both references Erik
// sent are photogrammetry bakes — 8192px UV atlases keyed to somebody else's
// unwrap, with seams, and with the sun of the afternoon they were captured
// baked into the pixels. Our terrain has no matching unwrap to receive them,
// and our sun MOVES: the same plot is lit at Ls 120 at 08:23 and the shadows
// are computed by marching the real height field. Draping a photograph with
// its own shadows already in it would fight that at every hour except the one
// it was taken. The same argument that made us mint the register textures
// rather than buy them.
//
// So the references are used as references. What they show at metre scale is
// unmistakable and it is not colour: it is AEOLIAN RIPPLES — regular parallel
// bedforms marching across everything flat enough to hold sand — with pale
// clast patches and a smooth dust drape between them.
//
// ── What drives it ──────────────────────────────────────────────────────────
//
// Every term is tied to something the bake already measured, because a
// procedural surface invented independently of the data would drift out of
// agreement with the panel next to it:
//
//   wind      the REGIONAL DOWNSLOPE, sampled wide off the height field.
//             Slope winds dominate local aeolian transport, so ripples run
//             transverse to the fall line — and because the fall line varies
//             across a plot, the ripple field bends with the terrain for free
//             instead of being one flat direction stamped over everything.
//             Where there is no fall line to speak of, the sets break into
//             domains of three crest directions instead.
//   sand      slope. Loose material does not sit on steep ground, so ripples
//             fade out above about 20 degrees and the bedrock beneath shows.
//   clasts    the MEASURED rock abundance k, the same number driving the
//             boulder scatter. The pebbles too small to be instanced become
//             speckle at the same density as the boulders that are.
//   colour    the ortho, unchanged. This modulates what is measured; it never
//             replaces it.
//
// Everything is in world metres off vW.xz, so there are no UVs to unwrap and
// no seams to hide, and it fades with distance both because centimetre detail
// is invisible at 400 m and because drawing it there is how you alias.

export const REGOLITH_GLSL = /* glsl */ `
float rgHash(vec2 p){
  p = fract(p * vec2(0.1031, 0.1030));
  p += dot(p, p.yx + 33.33);
  return fract((p.x + p.y) * p.x);
}
float rgNoise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(rgHash(i), rgHash(i + vec2(1.0, 0.0)), f.x),
             mix(rgHash(i + vec2(0.0, 1.0)), rgHash(i + vec2(1.0, 1.0)), f.x), f.y);
}
float rgFbm(vec2 p){
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++){ v += a * rgNoise(p); p *= 2.03; a *= 0.5; }
  return v;
}

/**
 * Ripple field height, in metres, at a point — and its gradient.
 *
 * Two sets, as on Mars: metre-scale transverse ripples with a finer set
 * riding on them. The phase is warped by low-frequency noise so the crests
 * meander the way a real bedform does; perfectly straight ripples read as
 * corduroy and are the single fastest way to make this look printed on.
 */
/**
 * Retire a band of detail as its wavelength approaches the pixel.
 *
 * Distance thresholds were the first attempt and they are the wrong ruler:
 * what actually matters is how many pixels a ripple covers, and that depends
 * on field of view, on resolution, and above all on the grazing angle — ground
 * near the horizon compresses enormously while its DISTANCE changes slowly.
 * Hand-tuned metres cannot express that, which is why a fine set tuned to look
 * right underfoot was still at a third of its strength where it covered four
 * pixels, weaving a cross-hatch across the middle distance.
 *
 * fwidth gives the footprint directly, so each band retires exactly when it
 * stops being representable, at any resolution and any angle.
 */
float rgBand(float lambda, float footprint){
  // Retire between THIRTY-TWO pixels per wavelength and twelve.
  //
  // Nyquist says two pixels can represent a wave, and Nyquist is about
  // reconstructing a signal, not about a shaded surface looking right. What is
  // drawn here is not the wave, it is the LIGHT ON the wave — a nonlinear
  // function of its slope — and that has energy well above the wave's own
  // frequency. Sixteen pixels was not enough; measured against a supersampled
  // reference the ground was still inventing a third of its own contrast.
  return 1.0 - smoothstep(lambda * 0.031, lambda * 0.083, footprint);
}

vec2 rgTurn(vec2 w, float a){ return w * cos(a) + vec2(-w.y, w.x) * sin(a); }

/**
 * Shares of three crest directions 60 deg apart at a point, from value noise
 * at s cycles per metre: soft domains, each owning about a third of the ground.
 */
vec3 rgDomains(vec2 P, float s){
  // Turned off the noise lattice and warped, or the domains tile like parquet.
  vec2 q = mat2(0.8, -0.6, 0.6, 0.8) * P * s;
  q += 1.3 * vec2(rgNoise(q * 1.7 + 3.1), rgNoise(q * 1.7 + 8.3));
  float n = 0.65 * rgNoise(q) + 0.35 * rgNoise(q * 2.1 + 7.7);
  float t = n * 12.566371;
  vec3 c = max(vec3(0.0), cos(vec3(t, t - 2.0943951, t + 2.0943951)));
  return c / max(c.x + c.y + c.z, 1e-4);
}

/**
 * fall is the regional downslope as a gradient (rise over run), not a unit
 * vector: its length says how far the fall line can be trusted to steer.
 */
vec3 rgRipples(vec2 P, vec2 fall, float scale, float footprint){
  vec2 w = length(fall) > 1e-6 ? normalize(fall) : vec2(1.0, 0.0);
  // A slope wind needs a slope; under about two degrees the field breaks into domains.
  float flatW = 1.0 - smoothstep(0.035, 0.08, length(fall));
  float phase = dot(P, w);
  // Meander: displace the phase, not the direction, so crests stay parallel
  // locally while the whole field wanders.
  //
  // Heavy warp. Ripples that stay in phase over tens of metres read as
  // corduroy — a woven texture rather than a wind-built one — and at a low sun
  // that is the first thing the eye rejects.
  float warp = (rgFbm(P * 0.045) - 0.5) * 3.4 + (rgFbm(P * 0.011) - 0.5) * 9.0;

  // ── the hierarchy, in METRES ─────────────────────────────────────────────
  // Mars has three bedform scales, and each one gets its real height:
  //
  //   TARs        5-40 m long, ~0.5 m high
  //   megaripples 1-5 m long,  0.05-0.15 m high
  //   ripples     0.1-0.5 m long, a centimetre or so
  //
  // These being real numbers is not pedantry, it is the whole fix. The first
  // version had no heights at all: it built unit sines and multiplied their
  // GRADIENT by a number chosen to make the ground look interesting. That
  // number put 36 degree slopes on a 2.35 m ripple — a 0.27 m dune where Mars
  // has a 7 cm one. Micro-relief that violent swings the surface normal
  // through tens of degrees between adjacent pixels, and no amount of
  // band-limiting can rescue an image whose true signal is that extreme. Every
  // frequency threshold tried before this was treating a symptom.
  float lam0 = 13.0;   float h0 = 0.50;
  float lam1 = 2.35;   float h1 = 0.07;
  float lam2 = 0.42;   float h2 = 0.008;

  float b0 = rgBand(lam0, footprint);
  float b1 = rgBand(lam1, footprint);
  float b1h = rgBand(lam1 * 0.5, footprint);
  float b2 = rgBand(lam2, footprint);

  // SLOPE retires long before HEIGHT does, so it gets its own bands, evaluated
  // as though every wavelength were three times shorter than it is.
  //
  // This is where the moire actually lived. Disabling the normal perturbation
  // at the worst pose took the error from 9.1 grey levels to 0.6 — the whole
  // artifact was in the shading of the slope, not in the shape — and 80% of
  // that was the megaripple band alone, at a footprint where its HEIGHT was
  // already fully retired. A band contributing nothing visible to the surface
  // was still driving the light across it, because what gets drawn is a
  // nonlinear function of the gradient and carries energy far above the
  // wavelength that produced it.
  //
  // Retiring height and slope on one schedule is what made every earlier
  // attempt a choice between a flat foreground and a moire middle distance.
  // They are separate now, so the near view keeps its relief and the shading
  // gives up early.
  float q0 = rgBand(lam0 * 3.0, footprint);
  float q1 = rgBand(lam1 * 3.0, footprint);
  float q1h = rgBand(lam1 * 1.5, footprint);
  float q2 = rgBand(lam2 * 3.0, footprint);

  float k0 = 6.2831853 / lam0;
  float k1 = 6.2831853 / lam1;
  float k2 = 6.2831853 / lam2;

  float a0 = (phase + warp * 0.5) * k0;
  float a1 = (phase + warp) * k1;
  // The fine set rides at a slight angle to the coarse, as it does in the field.
  vec2 w2 = rgTurn(w, 0.22);
  float a2 = (dot(P, w2) + warp * 0.35) * k2;

  // On flat ground each set breaks into three crest directions 60 deg apart, so no sun azimuth picks one out.
  vec3 d1 = vec3(1.0, 0.0, 0.0);
  vec3 d2 = vec3(1.0, 0.0, 0.0);
  float a1b = 0.0, a1c = 0.0, a2b = 0.0, a2c = 0.0;
  vec2 w1b = w, w1c = w, w2b = w2, w2c = w2;
  if (flatW > 0.0) {
    d1 = mix(d1, rgDomains(P, 0.08), flatW);
    d2 = mix(d2, rgDomains(P + 19.3, 0.45), flatW);
    w1b = rgTurn(w, 1.0471976);
    w1c = rgTurn(w, -1.0471976);
    w2b = rgTurn(w2, 1.0471976);
    w2c = rgTurn(w2, -1.0471976);
    a1b = (dot(P, w1b) + warp + 5.3) * k1;
    a1c = (dot(P, w1c) + warp + 2.9) * k1;
    a2b = (dot(P, w2b) + warp * 0.35 + 1.7) * k2;
    a2c = (dot(P, w2c) + warp * 0.35 + 0.6) * k2;
  }

  // Asymmetric profile: a real ripple has a long stoss slope and a short lee.
  // The harmonic is half the wavelength and retires on its own schedule.
  float s0 = sin(a0) * b0;
  float s1, s2;
  if (flatW > 0.0) {
    vec3 s1v = sin(vec3(a1, a1b, a1c)) * b1 + sin(2.0 * vec3(a1, a1b, a1c)) * 0.15 * b1h;
    s1 = dot(s1v, d1);
    s2 = dot(sin(vec3(a2, a2b, a2c)), d2) * b2;
  } else {
    s1 = sin(a1) * b1 + sin(2.0 * a1) * 0.15 * b1h;
    s2 = sin(a2) * b2;
  }

  // Ripples only exist where there is sand to build them. NOT named "patch":
  // that is a reserved word in GLSL and the shader silently fails to compile.
  //
  // The THRESHOLD IS FILTERED, and this was the whole moire.
  //
  // Every band above retires politely as the pixels give out, and this mask
  // then multiplied all of them — with a hard smoothstep over value noise,
  // which has corners, so its edges carry energy down to a metre or two no
  // matter how coarse the ripples underneath have become. Measured at the
  // worst pose, the megaripple band read 0.00 and the ridge band 1.00, while
  // the error on screen had a two-to-five metre period: the pattern was
  // entirely the mask's edges, and every fix aimed at the ripples missed it.
  // Three separate attempts to tighten the ripple bands failed here, and one
  // of them cost most of the near-field detail before the debug channels said
  // where the energy actually was.
  //
  // Widening the transition with the footprint is the analytic way to filter a
  // threshold: as a pixel covers more ground it sees more of both sides of the
  // edge, so the edge should soften rather than stay a cliff.
  float edge = 0.19 + clamp(footprint * 1.2, 0.0, 0.45);
  float sandPatch = smoothstep(0.53 - edge, 0.53 + edge, rgFbm(P * 0.021 + 11.3));
  float amp = scale * sandPatch;
  float h = (s0 * h0 + s1 * h1 + s2 * h2) * amp;

  // True analytic gradient of exactly the height above — no free multiplier,
  // and retired on the slope schedule rather than the height one.
  float g0 = cos(a0) * k0 * h0 * q0;
  vec3 g1 = (cos(vec3(a1, a1b, a1c)) * q1 + cos(2.0 * vec3(a1, a1b, a1c)) * 0.30 * q1h) * k1 * h1 * d1;
  vec3 g2 = cos(vec3(a2, a2b, a2c)) * k2 * h2 * q2 * d2;
  vec2 grad = (w * g0 + w * g1.x + w1b * g1.y + w1c * g1.z
               + w2 * g2.x + w2b * g2.y + w2c * g2.z) * amp;
  return vec3(h, grad);
}
`;

const HEIGHT_GLSL = /* glsl */ `
uniform sampler2D uHeight;
uniform vec2  uBBox;        // metres across the height texture
uniform vec2  uTexel;       // texture size in texels
uniform float uRelief;      // metres represented by 0..1
uniform float uExag;        // vertical exaggeration
uniform float uLod;         // mip the geometry should read (buildHeightMips, plotField.ts)

#ifdef MANUAL_BILINEAR
// Some drivers lack OES_texture_float_linear, and three quietly falls back to
// NEAREST — which on a height field is metre-high stair-steps across the whole
// plot. Filter by hand rather than ship that silently.
float texBil(sampler2D t, vec2 uv){
  vec2 p = uv * uTexel - 0.5;
  vec2 i = floor(p), f = fract(p);
  vec2 b = (i + 0.5) / uTexel;
  vec2 e = 1.0 / uTexel;
  float h00 = textureLod(t, b, 0.0).r;
  float h10 = textureLod(t, b + vec2(e.x, 0.0), 0.0).r;
  float h01 = textureLod(t, b + vec2(0.0, e.y), 0.0).r;
  float h11 = textureLod(t, b + vec2(e.x, e.y), 0.0).r;
  return mix(mix(h00, h10, f.x), mix(h01, h11, f.x), f.y);
}
// Without OES_texture_float_linear there is no filtered sampling to mip into,
// so the hand-rolled path stays on level 0.
#define SAMPLE_H(uv) texBil(uHeight, uv)
#define SAMPLE_H_LOD(uv, lod) texBil(uHeight, uv)
#else
#define SAMPLE_H(uv) textureLod(uHeight, uv, 0.0).r
#define SAMPLE_H_LOD(uv, lod) textureLod(uHeight, uv, lod).r
#endif

vec2 plotUv(vec2 p){ return p / uBBox + 0.5; }

/** Elevation in metres above the tile minimum, with exaggeration applied. */
float plotH(vec2 p){
  vec2 uv = clamp(plotUv(p), 0.0005, 0.9995);
  return SAMPLE_H(uv) * uRelief * uExag;
}

/** The same, read at the mip the vertex lattice can actually represent. */
float plotHGeo(vec2 p){
  vec2 uv = clamp(plotUv(p), 0.0005, 0.9995);
  return SAMPLE_H_LOD(uv, uLod) * uRelief * uExag;
}
`;

// ─────────────────────────────────────────────────────────────────────────────
// The plot
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The scattering chunk, shared with the tiles above the claim edge.
 *
 * `sharedUniforms` carries `uAtmoSunIrradiance`, `uAtmoAerialOff`, `uAtmoAerialTailOnGround`, and `uAtmoDeckSkyLight`, set once
 * a frame by src/atlas.ts - these uniforms are the same the tile material reads, so one flag
 * turns the aerial term off on both at once for the negative control. Unlike
 * the tiles, the plot needs no per-frame frame-transform: its own frame is
 * already y-up local metres, which is `atmoAerialPerspective`'s local-horizon
 * convention with the identity basis, so `-V` and `uSunDir` (both already in
 * that frame) are passed straight through.
 */
export interface PlotAtmoRef {
  readonly atmo: AtmosphereHandle;
  readonly sharedUniforms: Record<string, THREE.IUniform>;
}

/**
 * The cascaded shadow map chunk, shared by the plot's three materials.
 *
 * `uniforms` is owned by src/atlas.ts - the three lights, their shadow
 * cameras and the render targets they draw into all live there, next to the
 * rover and rock scatter whose `castShadow` this same edge flips. The plot
 * only ever samples, through `PLOT_CSM_GLSL` below.
 */
export interface PlotShadowRef {
  readonly uniforms: Record<string, THREE.IUniform>;
}

/** The terrain albedo ramp's two ends, dark to bright, in the ortho's 0..1 domain. */
export interface AlbedoRamp {
  readonly low: readonly [number, number, number];
  readonly high: readonly [number, number, number];
}

/** A neutral grey ramp for a body whose atmosphere block declares none. */
const DEFAULT_ALBEDO_RAMP: AlbedoRamp = {
  low: [0.15, 0.15, 0.15],
  high: [0.55, 0.55, 0.55],
};

const luminance = (c: readonly number[]): number => (
  0.2126 * (c[0] ?? 0) + 0.7152 * (c[1] ?? 0) + 0.0722 * (c[2] ?? 0));

/** The same colour with no hue left in it. */
const neutral = (c: readonly number[]): [number, number, number] => {
  const y = luminance(c);
  return [y, y, y];
};

/**
 * The colour the walkaround draws a plot's ground at: the leaves' own, outright.
 *
 * This used to be a LEVELLING against a curve, because the three materials here
 * wrote `gl_FragColor` with no tone-mapping chunk on it: the same linear albedo
 * that the tiles' MeshStandardMaterial carried through ACES and the sRGB encode
 * landed raw in the buffer and read 3.7x darker, so a curve stood in for the
 * missing transfer and Mars's iron-oxide pair was that curve. The three
 * materials are on the renderer's own tone curve and output colour space now
 * (`#include <tonemapping_fragment>` / `<colorspace_fragment>` below), so the
 * inherited albedo is the answer on every body and the curve is gone with the
 * fault it was hiding.
 */
function inheritedTint(tint: readonly number[] | null): THREE.Vector3 {
  if (!tint) return new THREE.Vector3(0, 0, 0);
  return new THREE.Vector3(tint[0] ?? 0, tint[1] ?? 0, tint[2] ?? 0);
}

/**
 * The colours this plot is lit and painted with, from the body's own row.
 *
 * Every one of these was a literal reached on every body's path. A body whose
 * row declares no palette gets `neutralPalette`: the same luminance with the
 * hue taken out, and its own albedo ramp wherever the constant was a ground
 * colour rather than a light. See `PlotPalette` in src/world/types.ts.
 */
export interface PlotPaletteRef {
  readonly sun: readonly [number, number, number];
  readonly sky: readonly [number, number, number];
  readonly bounce: readonly [number, number, number];
  readonly wallFill: readonly [number, number, number];
  readonly wallEdge: readonly [number, number, number];
  readonly wallLow: readonly [number, number, number];
  readonly wallHigh: readonly [number, number, number];
  readonly plainLow: readonly [number, number, number];
  readonly plainHigh: readonly [number, number, number];
}

/**
 * Every colour a plot is lit and painted with, with the hue taken out.
 *
 * The luminance of each is exactly the literal it replaces, so a body that
 * declares no palette is drawn at the brightness it always was and nothing but
 * the Martian cast is gone. The numbers are Mars's, and Mars's own row carries
 * them now (`plot_palette` in public/art/atlas/bodies.json).
 */
export function neutralPalette(): PlotPaletteRef {
  return {
    sun: neutral([1.32, 1.02, 0.78]),
    sky: neutral([0.30, 0.22, 0.16]),
    bounce: neutral([0.20, 0.125, 0.088]),
    wallFill: neutral([0.16, 0.115, 0.095]),
    wallEdge: neutral([0.40, 0.23, 0.14]),
    wallLow: neutral([0.105, 0.058, 0.045]),
    wallHigh: neutral([0.52, 0.325, 0.215]),
    plainLow: neutral([0.155, 0.092, 0.070]),
    plainHigh: neutral([0.52, 0.325, 0.225]),
  };
}

/**
 * `plotHGeo`'s mip lookup: GL level 0 is the field itself and `mips[i]` is
 * level i + 1. A plain function rather than a per-call closure, and it
 * returns the sampled height directly rather than a level object, since
 * `meshHeightAt` calls into this on the order of 10^5 times on a band
 * repopulation frame.
 */
function sampleHeightLevel(f: PlotField, i: number, u: number, v: number): number {
  if (i <= 0) {
    return bilinearSample(f.norm, f.width, f.texHeight, u * f.width - 0.5, v * f.texHeight - 0.5);
  }
  const m = f.mips[Math.min(i, f.mips.length) - 1]!;
  return bilinearSample(m.data, m.width, m.height, u * m.width - 0.5, v * m.height - 0.5);
}

/**
 * What a plot's lattice is, before anything builds it.
 *
 * The caller needs the same three numbers the constructor would derive so it
 * can ask the worker for the buffers first; deriving them twice is how the two
 * threads would come to disagree, so they are derived once here.
 */
export interface LatticeSpec {
  readonly radiusM: number;
  readonly rings: number;
  readonly places: LatticePlace[];
  readonly origin: { x: number; z: number };
}

export function plotLatticeSpec(field: PlotField, detail?: number): LatticeSpec {
  const R = field.meta.hex.radius_m;
  const places = field.placements;
  // Tessellation budget is shared across the cluster, not spent per plot: a
  // sixteen-hex chain at the single-plot density would be 1.8M vertices.
  //
  // The budget IS the bound - 3N^2 vertices per hex times the cluster size is
  // held at 500k however the plots are arranged. The extra min(192, ...) on top
  // of it only ever bit clusters of four or fewer, which is to say the single
  // detail plot, and there it threw away three quarters of the budget: 192
  // rings where the budget allowed 408. That was invisible while the tiles were
  // 3.25 m/px, because the geometry was reading a mip coarser than the lattice
  // anyway. Now that the tiles are source-exact it is the only thing standing
  // between the data and the silhouette, so the cap goes and the budget governs
  // alone.
  const rings = detail ?? Math.max(48, Math.round(Math.sqrt(500_000 / (3 * places.length))));
  // The baker reports hex centres relative to the REGION centre; the vertex
  // lattice is built from axial coords. Reconcile once, here.
  const p0 = places[0]!;
  return {
    radiusM: R,
    rings,
    places: places.map((p) => ({ q: p.q, r: p.r })),
    origin: {
      x: p0.x_m - R * SQRT3 * (p0.q + p0.r / 2),
      z: p0.z_m - 1.5 * R * p0.r,
    },
  };
}

export interface PlotOptions {
  /** Lattice rings. 192 gives ~111k verts and a silhouette that holds up close. */
  detail?: number;
  /** The surface lattice, already built - by the tile worker, off the main
   *  thread (`plotLatticeSpec` says what to ask it for). Absent builds it here,
   *  which is the synchronous fallback and the `plot-mainthread` control. */
  lattice?: LatticeBuffers;
  /** Vertical exaggeration. Real Mars is flatter than anyone believes. */
  exaggeration?: number;
  wallDepthFraction?: number;
  /** The scattering chunk. Null falls back to no aerial term at all - a body
   *  with no atmosphere, or a caller too early for one to exist yet. */
  atmo?: PlotAtmoRef | null;
  /** The cascaded shadow chunk. Null draws with no cascades at all - a body
   *  with no walkaround yet, or a caller too early for the lights to exist. */
  shadow?: PlotShadowRef | null;
  /** Iron-oxide ramp endpoints, from the body's own atmosphere block.
   *  Defaults to a neutral grey pair. */
  albedoRamp?: AlbedoRamp;
  /** The body's own light and its plinth and plain colours. Defaults to
   *  `neutralPalette`, which is Mars's light with the hue taken out. */
  palette?: PlotPaletteRef;
  /** The body's equatorial radius in metres - the same radius the tile pyramid
   *  is addressed on, since the plain the shader curves is that pyramid's
   *  ground. Required: a default here would be Mars's. */
  curveRadiusM: number;
  /** The body's season, solar day and dust curve. Required for the same
   *  reason. */
  climate: PlotClimate;
  /** False on a body with no air, whose ground has no wind to build a ripple.
   *  Required, like climate. */
  bedforms: boolean;
  /**
   * The colour the pyramid draws this ground with, linear rgb.
   *
   * The orthoimage is one greyscale band, so a tint is not optional - the
   * question is only whose, and it is the leaves' own, hue and level together
   * (`inheritedTint`). Absent, the body's own albedo ramp supplies both.
   */
  tint?: readonly [number, number, number];
  /** The pyramid level of the leaf `tint` was read off. */
  tintLevel?: number;
  /** The orthoimage's own mean, raw, the way this shader samples it. */
  orthoMean?: number;
  /**
   * The same image low-passed at the inherited colour product's texel.
   *
   * Present, the shader shades the tint by `ortho / lowPass(ortho)`, which is
   * what a leaf inside the footprint does (src/atlas/tileMesh.ts). Absent, it
   * falls back to `orthoMean`, which is that ratio's coarse term replaced by
   * one number for the whole region.
   */
  orthoLowPass?: THREE.Texture | null;
  /** The focused body's own rock fit, off its registry row's `bake.rocks`.
   *  Absent or null is a body with no fit: no scatter and no clast lag. */
  rockLaw?: RockLaw | null;
}

/**
 * The clast lag's abundance: the pebbles too small to instance.
 *
 * Zero where the bake counted nothing, so a body with no fitted law gets no
 * speckle either. This used to fall back to 0.06, which is a Martian abundance
 * standing in for an unmeasured one.
 */
function rockLagK(rocks: PlotMeta['rocks'], law: RockLaw | null): number {
  if (!rocks || law === null || rocks.k_source === 'none') return 0;
  return rocks.k;
}

/**
 * One of the plot's three programs, built from its own source at module level.
 *
 * Not inline in the constructor, so `warmPlotPrograms` can build the same
 * material from the same strings: three keys a program by its shader SOURCE,
 * so a prototype that differs by one character links a second program instead
 * of reusing the one the plot will draw with.
 */
function plotSurfaceMaterial(
  uniforms: Record<string, THREE.IUniform>,
  defines: { MANUAL_BILINEAR?: number },
  atmoRef: PlotAtmoRef | null, shadowRef: PlotShadowRef | null,
): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms,
    defines,
    vertexShader: /* glsl */ `
      ${HEIGHT_GLSL}
      varying vec3 vW;
      varying vec2 vUv;
      ${shadowRef ? 'varying vec3 vWorldPos;' : ''}
      #include <logdepthbuf_pars_vertex>
      bool isPerspectiveMatrix(mat4 m) { return m[2][3] == -1.0; }
      void main(){
        vec3 p = position;
        // Geometry reads the filtered level; the fragment shader still reads
        // level 0, so the surface keeps its detail in shading while its
        // SHAPE stops chasing features the triangles cannot hold.
        p.y = plotHGeo(p.xz);
        vW = p;
        vUv = plotUv(p.xz);
        ${shadowRef ? 'vWorldPos = (modelMatrix * vec4(p, 1.0)).xyz;' : ''}
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
        #include <logdepthbuf_vertex>
      }`,
    fragmentShader: /* glsl */ `
      precision highp float;
      ${HEIGHT_GLSL}
      uniform sampler2D uAlbedo, uOverlay;
      uniform vec3  uSunDir;
      uniform float uSunFlux, uContour, uContourInterval;
      uniform vec3 uGridMix;
      uniform float uHasAlbedo, uHexR, uTime;
      uniform vec3 uTint, uTintGain;
      uniform float uHasTint, uOrthoMean, uHasOrthoLow, uFarCutM, uHasTintMap;
      uniform sampler2D uOrthoLow, uTintMap;
      uniform vec3 uSelect, uCamLocal;
      uniform float uSelMode;
      uniform vec3 uAlbedoLow, uAlbedoHigh;
      uniform vec3 uSunCol, uSunTint, uSkyCol, uBounceCol;
      uniform vec4 uPad;
      uniform vec4 uPads[8];
      uniform int uPadCount;
      uniform vec2 uLatOrigin;
      uniform vec3 uHexGrid;
      uniform sampler2D uClaims;   // parent R, child R, rings
      ${HEX_GRID_GLSL}
      ${atmoRef ? `${atmoRef.atmo.glsl}\n${ATMO_PLOT_DECL_GLSL}` : ''}
      ${REGOLITH_GLSL}
      ${GROUND_SPECULAR_GLSL}
      ${atmoRef ? SEA_GLSL : ''}
      uniform float uRockK, uRegolith, uBedforms, uRippleFall;
      ${shadowRef ? PLOT_CSM_GLSL : ''}
      varying vec3 vW;
      varying vec2 vUv;
      ${shadowRef ? 'varying vec3 vWorldPos;' : ''}

      /** The transfer a tagged sRGB sampler applies, for a texture read raw. */
      float srgbLin(float c){
        return c <= 0.04045 ? c / 12.92 : pow((c + 0.055) / 1.055, 2.4);
      }

      #include <logdepthbuf_pars_fragment>
      void main(){
        #include <logdepthbuf_fragment>
        // Past here the leaves own the ground: the bake is a PLANE and the
        // body is a sphere, so this mesh stands d^2/2R over the leaves and
        // that error moves the ground on screen by d/2R radians. uFarCutM is
        // where that reaches one pixel of the frame - out to it the measured
        // mesh is the better answer and owns the pixel, past it the plane is
        // drawing a horizon the body does not have. The second pass cuts
        // depth, so a discarded fragment shows the leaves behind it rather
        // than a hole (drawFrame in src/atlas.ts).
        //
        // From the EYE's own ground point, because vW is metres from the
        // region FILE's centre and a walkaround is not standing at it: at
        // Jezero the cluster sits 8.2 km out in that frame against a 6.3 km
        // cut, so a radius drawn round the file's centre discarded the whole
        // ground the person was standing on and handed it to the leaves.
        vec2 vFromEye = vW.xz - uCamLocal.xz;
        if (dot(vFromEye, vFromEye) > uFarCutM * uFarCutM) discard;
        // ── normal from the height field itself, at texel scale ───────────
        // Sampling the SOURCE rather than interpolating vertex normals keeps
        // detail the 192-ring lattice is far too coarse to carry.
        vec2 e = uBBox / uTexel;
        float hL = plotH(vW.xz - vec2(e.x, 0.0));
        float hR = plotH(vW.xz + vec2(e.x, 0.0));
        float hD = plotH(vW.xz - vec2(0.0, e.y));
        float hU = plotH(vW.xz + vec2(0.0, e.y));
        vec3 N = normalize(vec3(hL - hR, 2.0 * e.x, hD - hU));

        // True (un-exaggerated) slope — this is what the panel quotes.
        vec3 Ntrue = normalize(vec3((hL - hR) / uExag, 2.0 * e.x, (hD - hU) / uExag));

        // ── albedo ────────────────────────────────────────────────────────
        float ortho = uHasAlbedo > 0.5 ? texture2D(uAlbedo, vUv).r : 0.5;
        // HiRISE RED is a single greyscale band. Mars's colour is a property
        // of the dust, not of this image, so tint rather than pretend - and
        // the tint is the colour the PYRAMID draws this same ground with,
        // shaded by the orthoimage against its own mean, which is what
        // tileMesh.ts does over the leaves this mesh replaces. One product,
        // one colour, whichever module draws it. The body's own ramp is the
        // fallback for ground with no colour product over it - on Mars, iron
        // oxide to dark basaltic sand, bracketing the 0.25 Bond albedo. ONE
        // mix and no second multiply - scaling by the same channel twice was
        // blowing the highlights orange and making basalt country look like
        // lava.
        // Against a LOW-PASS of this same image and not against one number
        // for the region: the tint is the colour the leaves inherit, which
        // is a 163 m product, so everything coarser than that already lives
        // in the tint and only what is finer belongs to the orthoimage. The
        // mean puts the region's coarse structure back on top of it, and at
        // Hadley the ground in frame is 2.1x that mean - a level step at the
        // SURFACE edge with nothing in the light to explain it.
        float orthoRef = uHasOrthoLow > 0.5
          ? max(texture2D(uOrthoLow, vUv).r, 1e-3)
          : uOrthoMean;
        // Both decoded, because the leaves' pair is tagged sRGB and their
        // ratio is one of reflectances; this pair is read raw so that the
        // ramp branch below still gets the product's own bytes.
        float orthoRatio = uHasOrthoLow > 0.5
          ? srgbLin(ortho) / max(srgbLin(orthoRef), 1e-4)
          : ortho / orthoRef;
        vec3 tint = uHasTintMap > 0.5 ? texture2D(uTintMap, vUv).rgb * uTintGain : uTint;
        vec3 albedo = uHasTint > 0.5
          ? tint * clamp(orthoRatio, 0.0, 4.0)
          : mix(uAlbedoLow, uAlbedoHigh, ortho);

        // ── regolith: the surface below the photograph's resolution ───────
        // Fades in as the eye approaches, because centimetre detail is
        // invisible at four hundred metres and drawing it there is how you
        // alias a hillside into a shimmering mess.
        // Each band fades on ITS OWN scale, because they alias at different
        // distances. A 0.4 m ripple is finished as information by 35 m and is
        // pure noise past it; a 2.4 m ripple survives to a few hundred. Fading
        // them together is what turned the middle distance into a speckled
        // band — the frequency was still there long after the pixels could
        // hold it.
        float camD = length(uCamLocal - vW);
        // Metres of ground per pixel, right here. Every band of detail is
        // retired against THIS rather than against distance — see rgBand.
        float footprint = max(fwidth(vW.x), fwidth(vW.z));
        if (uRegolith < -0.5) {
          // Debug: show the footprint in metres per pixel, and the band
          // factor it produces for the coarse ripple set.
          gl_FragColor = vec4(clamp(footprint, 0.0, 1.0),
                              rgBand(2.35, footprint),
                              rgBand(0.42, footprint), 1.0);
          return;
        }
        float nearF = uRegolith * (1.0 - smoothstep(60.0, 260.0, camD));
        // Retired as though it were three times coarser than it is. The
        // clast lag is VALUE NOISE, and value noise has a corner in every
        // cell — its spectrum runs well past the nominal cell size, so
        // retiring it on that nominal figure leaves the corners aliasing
        // long after the feature itself is gone.
        float lagF = rgBand(0.40, footprint) * uRegolith;
        if (nearF > 0.002) {
          // Loose material does not sit on steep ground: above about twenty
          // degrees the sand is gone and what is left is the rock beneath.
          float slopeC = clamp(Ntrue.y, 0.0, 1.0);
          float sand = smoothstep(0.90, 0.985, slopeC);

          // With no air there is no wind and nothing builds a ripple; drawn
          // there, the field reads as parallel streaks under a low sun.
          if (uBedforms > 0.5) {
            // Wind runs down the regional fall line. Sampled WIDE, a hundred
            // texels out, so it follows the hillside the plot sits on rather
            // than the pebble underfoot, and bends across the plot with it.
            vec2 wd = vec2(plotH(vW.xz + vec2(e.x, 0.0) * 100.0) - plotH(vW.xz - vec2(e.x, 0.0) * 100.0),
                           plotH(vW.xz + vec2(0.0, e.y) * 100.0) - plotH(vW.xz - vec2(0.0, e.y) * 100.0));
            vec2 fall = -wd / (200.0 * e * uExag);
            if (uRippleFall > 0.5) fall /= max(length(fall), 1e-6);

            // Ripple amplitude is a few centimetres, and it is a NORMAL
            // perturbation rather than displacement: the mesh cannot carry a
            // 2 m wavelength and does not need to, because at this scale the
            // eye reads ripples entirely by how they catch the light.
            // The gradient is used AS IS. There is no artistic multiplier here
            // any more: the bedforms carry their real heights, so their real
            // slopes are what the light should see.
            vec3 rip = rgRipples(vW.xz, fall, sand * nearF, footprint);
            N = normalize(N + vec3(-rip.y, 0.0, -rip.z));
            // Crests are winnowed of dust and read a shade brighter.
            albedo *= 1.0 + clamp(rip.x, -0.25, 0.25) * 0.5;
          }

          // Clast lag: the pebbles too small to instance, at the same
          // abundance as the boulders that are. k is a fraction of ground
          // covered, so it maps straight onto how much speckle there is.
          // Eight per metre, not twenty-six — a pebble finer than the pixel
          // is not detail, it is noise, and it was reading as a dirty band
          // across the middle distance.
          if (lagF > 0.002) {
            float g = rgNoise(vW.xz * 8.0);
            float lag = smoothstep(1.0 - clamp(uRockK * 2.6, 0.02, 0.55), 1.0, g);
            albedo = mix(albedo, albedo * vec3(0.80, 0.78, 0.77), lag * 0.45 * lagF);
            N = normalize(N + vec3(rgNoise(vW.xz * 8.0 + 5.0) - 0.5, 0.0,
                                   rgNoise(vW.xz * 8.0 + 9.0) - 0.5) * lag * 0.6 * lagF);
          }

          // Where the sand has gone, the ground is darker and coarser.
          albedo *= mix(0.90, 1.0, sand);

          // Low-frequency mottling, so no two hundred metres of ground are
          // the same wash of colour. Small: this modulates a measurement.
          albedo *= 1.0 + (rgFbm(vW.xz * 0.012) - 0.5) * 0.16 * nearF;
        }

        // Cavity shading: compare the point against the neighbourhood a few
        // hundred metres out. Channel floors and crater interiors see less
        // sky than the plateau above them, and without this the fill light is
        // uniform and the relief goes soft.
        float wide = 0.0;
        for (int k = 0; k < 4; k++) {
          vec2 o = k == 0 ? vec2(1.0, 0.0) : k == 1 ? vec2(-1.0, 0.0)
                 : k == 2 ? vec2(0.0, 1.0) : vec2(0.0, -1.0);
          wide += plotH(vW.xz + o * e * 14.0);
        }
        float openness = clamp(0.5 + (plotH(vW.xz) - wide * 0.25) / (uRelief * uExag * 0.10), 0.15, 1.0);

        // ── horizon: does the sun clear the ridge to the east? ────────────
        // The same march the CPU runs to score every sub-plot's insolation.
        float shadow = 1.0;
        // Near zenith nothing can shadow anything: the longest shadow a 500 m
        // scarp can throw at 70 degrees elevation is under 200 m, and it is
        // already inside the first few steps. Skipping the march outright
        // there costs nothing visually and is most of a high-sun frame.
        if (uSunDir.y > 0.0 && uSunDir.y < 0.94) {
          vec2 dir = normalize(uSunDir.xz + vec2(1e-5));
          float tanA = uSunDir.y / max(length(uSunDir.xz), 1e-4);
          float h0 = plotH(vW.xz) + ${MARCH_START_LIFT_M.toFixed(1)};
          float d = e.x * 2.0, step = e.x * 2.0;
          // 26 steps growing at 1.14 reach as far as 40 at 1.09 did — the
          // march is geometric, so the tail is where the distance lives and
          // the near steps are the ones that cost.
          for (int i = 0; i < 26; i++) {
            vec2 p = vW.xz + dir * d;
            if (abs(p.x) > uBBox.x * 0.5 || abs(p.y) > uBBox.y * 0.5) break;
            float ht = plotH(p);
            // Softened by distance: a ridge 2 km away throws a fuzzier edge
            // than the boulder at your feet, even in a thin atmosphere.
            shadow = min(shadow, clamp((h0 + d * tanA - ht) / (0.010 * d + 1.2), 0.0, 1.0));
            if (shadow <= 0.003) break;
            d += step; step *= 1.14;
          }
        } else if (uSunDir.y <= 0.0) {
          shadow = 0.0;
        }

        float ci = max(dot(N, uSunDir), 0.0);
        vec3 V = normalize(uCamLocal - vW);
        float ce = max(dot(N, V), 0.0);
        // Regolith is not Lambertian. Blend toward Lommel-Seeliger, which is
        // what keeps dusty ground from going plastic at grazing sun.
        float ls = ci / (ci + ce + 0.55);
        float diff = mix(ci, ls * 1.35, 0.55);

        // The rover, the rocks and any structure standing on this ground are
        // not in shadow above - the march only ever sees the heightfield -
        // so the direct term alone takes the minimum of the two visibilities.
        // shadow counts the sun from the geometric horizon up, the line the
        // CPU's insolation pass counts daylight from.
        ${shadowRef ? /* glsl */ `
        float sunVis = min(shadow, csmVisibility(vWorldPos, length(uCsmAnchor - vWorldPos)));
        ` : /* glsl */ `
        float sunVis = shadow;
        `}

        vec3 sunCol = uSunCol * uSunTint * uSunFlux * ${atmoRef ? 'uAtmoGroundScale' : '1.0'};
        vec3 col = albedo * diff * sunCol * sunVis;
        // The leaves' own specular lobe, on the irradiance the sun puts on this facet.
        col += sunCol * (3.141592653589793 * ci * sunVis) * groundSpecular(N, uSunDir, V, albedo);

        // ── sky ───────────────────────────────────────────────────────────
        // The dome's fill and the ground bounce, both the body's own: Mars's
        // air is full of dust and its butterscotch dome fills shadow far more
        // generously than the Moon's does, which is why lunar shadows are
        // black and Martian ones are merely dim. A row with no palette gets
        // the same levels with no hue in them (PlotPalette, world/types.ts).
        col += albedo * uSkyCol * (0.42 + 0.58 * max(N.y, 0.0)) * openness * (0.35 + 0.65 * uSunFlux);
        // Bounce off the surrounding regolith — also gated by openness, since
        // a slot canyon has less lit ground in view of it than a plain does.
        col += albedo * uBounceCol * (0.35 + 0.65 * shadow) * openness * uSunFlux;
        ${atmoRef ? /* glsl */ `
        // The leaves' water term written out: Lambert, three's GGX and the
        // mirrored sky, on the flat sea's own up rather than the regolith's normal.
        float seaM = seaMaskOf(albedo);
        if (seaM > 0.0) {
          vec3 seaA = seaBodyOf(albedo);
          vec3 seaN = vec3(0.0, 1.0, 0.0);
          float seaCi = max(uSunDir.y, 0.0);
          vec3 seaCol = seaA * seaCi * sunCol * sunVis
            + seaA * uSkyCol * openness * (0.35 + 0.65 * uSunFlux)
            + sunCol * (3.141592653589793 * seaCi * sunVis) * (uSeaFresnelOn > 0.5
              ? seaSpecular(seaN, uSunDir, V) : groundSpecular(seaN, uSunDir, V, seaA))
            + seaReflectedSky(seaN, V, uSunDir) * uAtmoSunIrradiance;
          col = mix(col, seaCol, seaM);
        }
        ` : ''}

        // ── contours ──────────────────────────────────────────────────────
        if (uContour > 0.001) {
          float hm = plotH(vW.xz) / uExag;
          float f = fract(hm / uContourInterval);
          float w = fwidth(hm / uContourInterval) * 1.6;
          float line = 1.0 - smoothstep(0.0, w, min(f, 1.0 - f));
          float idx = floor(hm / uContourInterval);
          // Every fifth contour is an index line, as on any survey sheet.
          float major = abs(mod(idx, 5.0)) < 0.5 ? 1.0 : 0.45;
          col = mix(col, vec3(0.95, 0.85, 0.62), line * major * 0.55 * uContour);
        }

        // ── buildability overlay ──────────────────────────────────────────
        if (uGridMix.x + uGridMix.y + uGridMix.z > 0.001) {
          // The TEXTURE supplies the verdict tint only. A tint may be soft;
          // a property line may not, so the lines are drawn from the lattice
          // below rather than read out of a 22 m-per-texel picture.
          vec4 ov = texture2D(uOverlay, vUv);
          col = mix(col, mix(col, ov.rgb, 0.46) * (0.84 + 0.38 * diff), ov.a * uGridMix.x);

          vec2 lp = vW.xz - uLatOrigin;
          float PR = uHexGrid.x, CR = uHexGrid.y, rings = uHexGrid.z;

          // Sub-cells are laid out per PARENT, so find the parent first and
          // evaluate the child lattice relative to it. A hexagon cannot be
          // tiled by smaller hexagons, so the children do not form one global
          // lattice and cannot be found in a single step.
          vec2 qr = hexAxial(lp, PR);
          vec2 pc = hexCentreOf(qr, PR);
          vec2 rel = lp - pc;
          // 255 held, 96 has a detail tile, 0 neither. Indexed by ground
          // position (vUv, the same one uHeight and uOverlay read) rather
          // than by the axial cell: an offset keyed to (q, r) clips past 32
          // plots from one bake origin, and one keyed to ground position
          // does not.
          float claimBits = texture2D(uClaims, vUv).r;
          float held = step(0.75, claimBits);
          float enterable = step(0.25, claimBits);
          vec2 cc = hexCentre(rel, CR);
          // Same containment test buildSubPlots applies when it lays them.
          bool cellExists = length(cc) <= PR - CR * 1.1 && hexSDF(cc, PR) < 0.0;
          if (cellExists && uGridMix.y > 0.001) {
            // Fade the fine grid out with distance. A cell edge is worth a
            // pixel when you are standing over it and is moire when thirty
            // plots of them are on screen at once.
            float dCam = length(uCamLocal - vW);
            float near = 1.0 - smoothstep(CR * 6.0, CR * 26.0, dCam);
            float cl = hexLine(hexSDF(rel - cc, CR), 1.0, vW.xz);
            col = mix(col, mix(col, ov.rgb, 0.85) * 1.25, cl * 0.55 * uGridMix.y * near);
          }

          // Parcel boundary on top: bone, thicker, and the line that matters
          // because it is the thing that gets bought.
          float pl = hexLine(hexSDF(rel, PR), held > 0.5 ? 2.4 : 1.5, vW.xz);
          // A held parcel wears gold and a heavier line. Ownership should be
          // visible from across the claim, not only in the panel.
          vec3 lineCol = mix(vec3(0.95, 0.90, 0.82), vec3(1.0, 0.80, 0.34), held);
          col = mix(col, lineCol, pl * (0.72 + 0.22 * held) * uGridMix.z);
          if (held > 0.5) {
            float inside = hexSDF(rel, PR) < 0.0 ? 1.0 : 0.0;
            col += vec3(0.30, 0.21, 0.07) * inside * 0.16 * uGridMix.z;
          }
          // A plot you can descend into wears a cool inner edge — enough to
          // pick out at a glance across the whole claim, not enough to
          // compete with ownership. Half the plots at Jezero lie beyond the
          // stereo footprint and hold nothing finer to arrive at; without
          // this, finding one of the others is a coin flip.
          if (enterable > 0.5 && held < 0.5) {
            float sd = hexSDF(rel, PR);
            float band = (sd < 0.0) ? exp(sd / (PR * 0.055)) : 0.0;
            col += vec3(0.16, 0.30, 0.38) * band * 0.55 * uGridMix.z;
          }
        }

        // ── selection ──────────────────────────────────────────────────────
        // Drawn from a uniform rather than repainted into the overlay: a
        // click should not cost a megapixel of CPU rasterisation, and the
        // ring wants to pulse anyway.
        if (uSelect.z > 0.0 && uSelMode > 0.5) {
          // The wash inside the cell, which the CELLS control drops when a
          // player would rather see the ground than the pick.
          float selFill = step(1.5, uSelMode);
          vec2 d = vW.xz - uSelect.xy;
          float r = uSelect.z;
          vec2 ad = abs(d);
          float inHex = (ad.x <= r * 0.8660254 && ad.y + ad.x * 0.5773503 <= r) ? 1.0 : 0.0;
          float edge = min(1.0 - ad.x / (r * 0.8660254),
                           1.0 - (ad.y + ad.x * 0.5773503) / r);
          float ring = inHex * (1.0 - smoothstep(0.0, 0.10, edge));
          // Never let the pulse reach zero — a selection that periodically
          // vanishes reads as a rendering fault, not as emphasis.
          float pulse = 0.78 + 0.22 * sin(uTime * 3.0);
          col = mix(col, vec3(1.0, 0.95, 0.74), ring * pulse);
          // Lift the whole cell a little so it separates from its neighbours
          // even when the rim is edge-on to the camera.
          col += vec3(0.34, 0.27, 0.16) * inHex * (1.0 - ring) * 0.34 * pulse * selFill;
          // And a halo just OUTSIDE the boundary, so the cell reads at any
          // zoom where the rim itself is under a pixel wide.
          float halo = (1.0 - inHex) * exp(-max(-edge, 0.0) * 26.0);
          col += vec3(0.55, 0.44, 0.24) * halo * 0.30 * pulse;

          // A 100 m grid inside the selection. Nothing conveys the size of a
          // piece of ground like counting something familiar across it, and
          // "seven squares wide" lands where "0.37 km2" does not.
          vec2 g = abs(fract(vW.xz / 100.0 + 0.5) - 0.5) * 100.0;
          float wpx = max(fwidth(vW.x), fwidth(vW.z));
          float gl = 1.0 - smoothstep(0.0, wpx * 1.6, min(g.x, g.y));
          col = mix(col, vec3(0.95, 0.93, 0.84), inHex * gl * 0.20 * selFill);
        }

        // ── structures ────────────────────────────────────────────────────
        // Hexagonal footprints. Solar arrays keep the row-stripe look; a
        // building's is a graded pad under its meshes (structureMeshes.ts)
        // with a thin line in its class colour.
        // P.w encodes class: 0 solar, 1 ext, 2 ref, 3 wrk, 4 batt, 5 smelt, 6 fab, 7 asm,
        // and past that any class CLASS_IDS does not list (UNKNOWN_CLASS_ID), in pewter.
        for (int i = 0; i < 8; i++) {
          if (i >= uPadCount) break;
          vec4 P = uPads[i];
          if (P.z <= 0.0) continue;
          vec2 d = vW.xz - P.xy;
          // Hex SDF: pointy-along-Z, matching insideHexZ and the terrain grid.
          // Vertices at (0, ±r), flat edges facing ±X.
          vec2 ad = abs(d);
          float apoth = P.z * 0.866025;
          float hexD = max(ad.y * 0.866025 + ad.x * 0.5, ad.x) - apoth;
          float cls = P.w;
          vec3 rimCol = cls < 0.5 ? vec3(0.98, 0.86, 0.55)
                      : cls < 1.5 ? vec3(0.40, 0.85, 0.80)
                      : cls < 2.5 ? vec3(0.95, 0.65, 0.30)
                      : cls < 3.5 ? vec3(0.70, 0.50, 0.90)
                      : cls < 4.5 ? vec3(0.45, 0.90, 0.40)
                      : cls < 5.5 ? vec3(1.00, 0.54, 0.31)
                      : cls < 6.5 ? vec3(0.70, 0.62, 0.86)
                      : cls < 7.5 ? vec3(0.44, 0.88, 0.75)
                      : vec3(0.78, 0.76, 0.70);
          float px = max(fwidth(vW.x), 1.0);
          if (cls > 0.5) {
            // A building's pad is graded, sintered regolith: the ground's own
            // tone flattened and a shade greyer, laid in 12 m sintered courses,
            // with a raked berm lip at the edge and one thin class line on it.
            // The building carries the colour, in its lights and trim; from
            // across the plot a faint wash of that colour still finds it.
            float farWash = smoothstep(2500.0, 6000.0, length(uCamLocal.xz - P.xy));
            if (hexD < P.z * 2.0) col += rimCol * exp(-max(hexD, 0.0) / (P.z * 0.5)) * 0.22 * farWash;
            // After dusk the plant's floodlights pool on its pad, brightest
            // among the parts, and spill a little past the lip.
            float night = 1.0 - smoothstep(-0.06, 0.14, uSunDir.y);
            if (night > 0.0 && hexD < 60.0) {
              float pool = 0.3 + 0.7 * exp(-dot(d, d) / (P.z * P.z * 0.2));
              col += vec3(0.20, 0.13, 0.075) * night * pool * exp(-max(hexD, 0.0) / 22.0);
            }
            if (hexD > 2.0) continue;
            float inside = 1.0 - smoothstep(-px * 1.5, 0.0, hexD);
            float L = dot(col, vec3(0.2126, 0.7152, 0.0722));
            vec3 sinter = mix(col, vec3(L) * vec3(1.10, 0.96, 0.84), 0.5) * 1.12;
            sinter *= 0.90 + 0.10 * smoothstep(0.0, P.z * 0.8, length(d));
            vec2 tg = abs(fract(d / 12.0) - 0.5) * 12.0;
            float joint = 1.0 - smoothstep(0.0, px * 1.2, min(tg.x, tg.y));
            sinter *= 1.0 - 0.07 * joint * (1.0 - smoothstep(150.0, 600.0, length(uCamLocal - vW)));
            col = mix(col, sinter, inside * 0.85);
            float lip = smoothstep(-9.0, -4.0, hexD) * (1.0 - smoothstep(-3.0, -0.5, hexD));
            col *= 1.0 - 0.22 * lip * inside;
            float line = smoothstep(-px * 2.2, -px * 0.6, hexD) * (1.0 - smoothstep(-px * 0.6, px * 0.4, hexD));
            col = mix(col, rimCol * 0.8 * (1.0 - 0.6 * night), line * 0.55);
            continue;
          }
          if (hexD > 2.0) continue;
          vec3 fill;
          if (cls < 0.5) {
            // Solar: panel row stripes
            float rows = abs(fract(vW.z / 22.0) - 0.5) * 2.0;
            fill = mix(vec3(0.09, 0.13, 0.24), vec3(0.20, 0.28, 0.44), rows);
          } else if (cls < 1.5) {
            // Extractor: teal
            fill = vec3(0.12, 0.32, 0.36);
          } else if (cls < 2.5) {
            // Refinery: warm orange
            fill = vec3(0.38, 0.22, 0.08);
          } else if (cls < 3.5) {
            // Workshop: violet
            fill = vec3(0.26, 0.14, 0.36);
          } else if (cls < 4.5) {
            // Battery: charge green
            fill = vec3(0.10, 0.30, 0.14);
          } else if (cls < 5.5) {
            // Smelter: furnace red
            fill = vec3(0.40, 0.14, 0.06);
          } else if (cls < 6.5) {
            // Fabricator: lilac
            fill = vec3(0.24, 0.20, 0.34);
          } else if (cls < 7.5) {
            // Assembly bay: sea green
            fill = vec3(0.08, 0.30, 0.26);
          } else {
            fill = vec3(0.22, 0.22, 0.21);
          }
          float inside = 1.0 - smoothstep(-px * 1.5, 0.0, hexD);
          // An array has no floodlights: after dusk its fill and rim go dark with the ground.
          float dusk = 1.0 - 0.85 * (1.0 - smoothstep(-0.06, 0.14, uSunDir.y));
          col = mix(col, fill * dusk, inside * 0.78);
          float rim = smoothstep(-px * 2.5, 0.0, hexD) * (1.0 - smoothstep(0.0, px * 1.5, hexD));
          col = mix(col, rimCol * dusk, rim * 0.9);
        }

        // The one under consideration: an outline only, so you can see what
        // you are about to commit to without it being committed.
        if (uPad.z > 0.0) {
          vec2 pd = vW.xz - uPad.xy;
          vec2 apd = abs(pd);
          float hexPrev = max(apd.y * 0.866025 + apd.x * 0.5, apd.x) - uPad.z * 0.866025;
          float w = max(fwidth(vW.x), 0.6) * 2.0;
          float edge = smoothstep(-w * 2.5, -w, hexPrev) * (1.0 - smoothstep(-w, 0.0, hexPrev));
          col = mix(col, vec3(0.62, 0.86, 0.96), edge * 0.75);
        }

        // ── aerial perspective ────────────────────────────────────────────
        // The Moon has none of this and REGOLITH is right to refuse it. Mars
        // is the opposite case: a dusty column with real optical depth, so
        // distance genuinely washes out — and it washes toward whatever the
        // sky is doing in that direction, including the blue near a low sun.
        // The same scattering LUT the sky dome and the tiles above the
        // claim edge read, so a hill at the rim of the plot fades toward
        // the exact colour the sky is showing there.
        ${atmoRef ? /* glsl */ `
        {
          vec3 atmoT = vec3(1.0);
          vec3 atmoS = vec3(0.0);
          if (uAtmoAerialOff < 0.5) {
            // This fragment IS ground, so the sky-convergence tail never fires
            // here, whatever the sphere gate would have said about the relief
            // it stands on. descent-dojo --inject aerial-tail-on-ground arms
            // uAtmoAerialTailOnGround to put the sphere gate back.
            bool fxEndsOnGround = uAtmoAerialTailOnGround < 0.5;
            atmoAerialPerspective(-V, uSunDir, length(uCamLocal - vW), fxEndsOnGround, atmoT, atmoS);
          }
          col = col * atmoT + atmoS * uAtmoSunIrradiance + (vec3(1.0) - atmoT) * uAtmoDeckSkyLight;
        }
        ` : ''}

        gl_FragColor = vec4(col, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
  });
}

/**
 * One of the plot's three programs, built from its own source at module level.
 *
 * Not inline in the constructor, so `warmPlotPrograms` can build the same
 * material from the same strings: three keys a program by its shader SOURCE,
 * so a prototype that differs by one character links a second program instead
 * of reusing the one the plot will draw with.
 */
function plotWallMaterial(
  uniforms: Record<string, THREE.IUniform>,
  defines: { MANUAL_BILINEAR?: number },
  atmoRef: PlotAtmoRef | null, shadowRef: PlotShadowRef | null,
): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms,
    defines,
    vertexShader: /* glsl */ `
      ${HEIGHT_GLSL}
      attribute float isTop;
      uniform float uWallDepth;
      varying vec3 vW;
      varying vec3 vN;
      varying float vT;
      ${shadowRef ? 'varying vec3 vWorldPos;' : ''}
      #include <logdepthbuf_pars_vertex>
      bool isPerspectiveMatrix(mat4 m) { return m[2][3] == -1.0; }
      void main(){
        vec3 p = position;
        float top = plotHGeo(p.xz);
        // Base is a CONSTANT plane, not top-minus-depth. A slab of uniform
        // thickness follows the terrain on its underside too, which reads as a
        // draped rug; a flat cut reads as a block of ground lifted out whole.
        p.y = mix(-uWallDepth, top, isTop);
        vT = isTop;
        vN = normal;
        vW = p;
        ${shadowRef ? 'vWorldPos = (modelMatrix * vec4(p, 1.0)).xyz;' : ''}
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
        #include <logdepthbuf_vertex>
      }`,
    fragmentShader: /* glsl */ `
      precision highp float;
      uniform vec3 uSunDir, uCamLocal;
      uniform float uSunFlux, uWallDepth, uReliefScale;
      uniform vec3 uSunCol, uSunTint, uWallFill, uWallEdge, uWallLow, uWallHigh;
      ${atmoRef ? `${atmoRef.atmo.glsl}\n${ATMO_PLOT_DECL_GLSL}` : ''}
      ${shadowRef ? PLOT_CSM_GLSL : ''}
      varying vec3 vW;
      varying vec3 vN;
      varying float vT;
      ${shadowRef ? 'varying vec3 vWorldPos;' : ''}

      float hash(vec2 p){ p = fract(p * vec2(0.1031, 0.1030)); p += dot(p, p.yx + 33.33); return fract((p.x + p.y) * p.x); }
      float noise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f*f*(3.0-2.0*f);
        return mix(mix(hash(i), hash(i+vec2(1,0)), f.x), mix(hash(i+vec2(0,1)), hash(i+vec2(1,1)), f.x), f.y); }

      #include <logdepthbuf_pars_fragment>
      void main(){
        #include <logdepthbuf_fragment>
        // Stratigraphy. The cut face is the argument that this is a solid
        // block of ground and not a decal: banded flows, dust-mantled, with
        // the freshest material at the top.
        //
        // Band frequency is expressed per unit of RELIEF, not per metre —
        // absolute frequencies turn into moire the moment the exaggeration
        // slider moves, which is how the first pass ended up looking like
        // corduroy.
        float depth = 1.0 - vT;
        float y = vW.y / max(uReliefScale, 1.0);
        float band = noise(vec2(y * 9.0, 0.0)) * 0.50
                   + noise(vec2(y * 31.0, 3.7)) * 0.32
                   + noise(vW.xz * 0.004 + y * 1.5) * 0.18;
        band = smoothstep(0.24, 0.78, band);
        vec3 col = mix(uWallLow, uWallHigh, band);
        // Darken downward — less light reaches the bottom of a cut.
        col *= mix(1.0, 0.40, depth * depth);

        // Light the face like the vertical rock it is: three walls catch the
        // sun, three are in their own shadow, and the terminator between them
        // is what makes the hexagon read as a block you could pick up.
        vec3 N = normalize(vN);
        float sun = max(dot(N, uSunDir), 0.0);
        ${shadowRef ? /* glsl */ `
        float sunVis = csmVisibility(vWorldPos, length(uCsmAnchor - vWorldPos));
        ` : /* glsl */ `
        float sunVis = 1.0;
        `}
        vec3 sunCol = uSunCol * uSunTint * uSunFlux * ${atmoRef ? 'uAtmoGroundScale' : '1.0'};
        vec3 lit = col * (0.10 + 0.90 * sun * sunVis) * sunCol;
        // Dome fill. A cut face sees roughly half the sky.
        lit += col * uWallFill * (0.30 + 0.70 * uSunFlux);
        // Sliver of bounce along the very top edge, only where lit. On a body
        // with no palette this is neutral: as a saturated iron-oxide constant
        // it drew a red-orange line along every ridge on the Moon.
        lit += uWallEdge * pow(vT, 18.0) * sun * uSunFlux;
        ${atmoRef ? /* glsl */ `
        {
          vec3 Vw = uCamLocal - vW;
          vec3 atmoT = vec3(1.0);
          vec3 atmoS = vec3(0.0);
          if (uAtmoAerialOff < 0.5) {
            // This fragment IS ground; see plotSurfaceMaterial's own comment.
            bool fxEndsOnGround = uAtmoAerialTailOnGround < 0.5;
            atmoAerialPerspective(normalize(-Vw), uSunDir, length(Vw), fxEndsOnGround, atmoT, atmoS);
          }
          lit = lit * atmoT + atmoS * uAtmoSunIrradiance + (vec3(1.0) - atmoT) * uAtmoDeckSkyLight;
        }
        ` : ''}
        gl_FragColor = vec4(lit, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
    side: THREE.DoubleSide,
  });
}

/**
 * One of the plot's three programs, built from its own source at module level.
 *
 * Not inline in the constructor, so `warmPlotPrograms` can build the same
 * material from the same strings: three keys a program by its shader SOURCE,
 * so a prototype that differs by one character links a second program instead
 * of reusing the one the plot will draw with.
 */
function plotGroundMaterial(
  uniforms: Record<string, THREE.IUniform>,
  atmoRef: PlotAtmoRef | null, shadowRef: PlotShadowRef | null,
): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms,
    vertexShader: /* glsl */ `
      uniform float uWallDepth, uCurveR;
      varying vec3 vW;
      ${shadowRef ? 'varying vec3 vWorldPos;' : ''}
      #include <logdepthbuf_pars_vertex>
      bool isPerspectiveMatrix(mat4 m) { return m[2][3] == -1.0; }
      void main(){
        vec3 p = position;
        float d = length(p.xz);
        // At a body's own radius a plain a few kilometres out has measurably
        // fallen away. Same drop the horizon actually has.
        p.y = -uWallDepth - (d * d) / (2.0 * uCurveR);
        vW = p;
        ${shadowRef ? 'vWorldPos = (modelMatrix * vec4(p, 1.0)).xyz;' : ''}
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
        #include <logdepthbuf_vertex>
      }`,
    fragmentShader: /* glsl */ `
      precision highp float;
      uniform vec3 uSunDir, uCamLocal;
      uniform float uSunFlux;
      uniform vec3 uTint;
      uniform float uHasTint;
      uniform vec3 uSunCol, uSunTint, uSkyCol, uPlainLow, uPlainHigh;
      ${atmoRef ? `${atmoRef.atmo.glsl}\n${ATMO_PLOT_DECL_GLSL}` : ''}
      ${GROUND_SPECULAR_GLSL}
      ${shadowRef ? PLOT_CSM_GLSL : ''}
      varying vec3 vW;
      ${shadowRef ? 'varying vec3 vWorldPos;' : ''}

      float h1(vec2 p){ p = fract(p * vec2(0.1031, 0.1030)); p += dot(p, p.yx + 33.33); return fract((p.x + p.y) * p.x); }
      float n2(vec2 p){ vec2 i = floor(p), f = fract(p); f = f*f*(3.0-2.0*f);
        return mix(mix(h1(i), h1(i+vec2(1,0)), f.x), mix(h1(i+vec2(0,1)), h1(i+vec2(1,1)), f.x), f.y); }

      #include <logdepthbuf_pars_fragment>
      void main(){
        #include <logdepthbuf_fragment>
        // Context, not terrain: no structure that would compete with the
        // measured ground it surrounds, but enough scale for the eye to read
        // it AS ground rather than as fog. Three octaves, widely spaced, so
        // there is something recognisable at every distance in frame.
        float m = n2(vW.xz * 0.00022) * 0.50
                + n2(vW.xz * 0.00090) * 0.32
                + n2(vW.xz * 0.00400) * 0.18;
        // The measured ground's own colour, spread over the same relative
        // range this plain always had: it surrounds the plot, so a plain on a
        // curve of its own reads as a different planet round the edge of one.
        vec3 albedo = uHasTint > 0.5
          ? uTint * mix(0.55, 1.45, m)
          : mix(uPlainLow, uPlainHigh, m);

        // A shallow normal off the same field, so the plain catches the sun
        // the way the plots do instead of reading as a flat card.
        // Forward differences against the mid octave we already sampled:
        // two extra noise evaluations rather than four, on a surface that
        // covers most of the screen.
        float e = 120.0;
        float m1 = n2(vW.xz * 0.00090);
        float mx = n2((vW.xz + vec2(e, 0.0)) * 0.00090) - m1;
        float mz = n2((vW.xz + vec2(0.0, e)) * 0.00090) - m1;
        vec3 N = normalize(vec3(-mx * 4.4, 1.0, -mz * 4.4));

        float diff = max(dot(N, uSunDir), 0.0);
        ${shadowRef ? /* glsl */ `
        float sunVis = csmVisibility(vWorldPos, length(uCsmAnchor - vWorldPos));
        ` : /* glsl */ `
        float sunVis = 1.0;
        `}
        vec3 col = albedo * diff * sunVis * uSunCol * uSunTint * uSunFlux
          * ${atmoRef ? 'uAtmoGroundScale' : '1.0'};
        col += uSunCol * uSunTint * uSunFlux * ${atmoRef ? 'uAtmoGroundScale' : '1.0'}
          * (3.141592653589793 * diff * sunVis)
          * groundSpecular(N, uSunDir, normalize(uCamLocal - vW), albedo);
        col += albedo * uSkyCol * (0.35 + 0.65 * uSunFlux);

        ${atmoRef ? /* glsl */ `
        {
          vec3 V = uCamLocal - vW;
          vec3 atmoT = vec3(1.0);
          vec3 atmoS = vec3(0.0);
          if (uAtmoAerialOff < 0.5) {
            // This fragment IS ground; see plotSurfaceMaterial's own comment.
            bool fxEndsOnGround = uAtmoAerialTailOnGround < 0.5;
            atmoAerialPerspective(normalize(-V), uSunDir, length(V), fxEndsOnGround, atmoT, atmoS);
          }
          col = col * atmoT + atmoS * uAtmoSunIrradiance + (vec3(1.0) - atmoT) * uAtmoDeckSkyLight;
        }
        ` : ''}
        gl_FragColor = vec4(col, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
  });
}

/** One mip level of a data texture: what a banded upload copies from. */
interface UploadLevel {
  data: Float32Array<ArrayBuffer> | Uint8Array<ArrayBuffer> | Uint16Array<ArrayBuffer>;
  width: number;
  height: number;
}

/**
 * How many bytes of one texture to hand the driver in one frame.
 *
 * The cost is not all on the calling thread: a `texSubImage2D` returns as soon
 * as the command buffer has it and the GPU service pays for it, so a run of
 * cheap-looking bands inside one frame still left a 77 ms interval with 29 ms
 * of JS in it. The band is therefore a per-FRAME budget - one band, then the
 * frame back - and 4 MB is what one frame of this ladder absorbs.
 */
const UPLOAD_BAND_BYTES = 4 << 20;

/** The levels `renderer.initTexture` would upload, or null for an image. */
function uploadLevelsOf(tex: THREE.Texture): UploadLevel[] | null {
  const mips = tex.mipmaps as unknown as UploadLevel[] | undefined;
  if (mips && mips.length > 0 && mips[0]?.data) return mips;
  const img = tex.image as UploadLevel | undefined;
  return img?.data && img.width > 0 && img.height > 0 ? [img] : null;
}

/** The GL enums a raw band upload needs, or null for a pair this cannot cut. */
function bandEnums(
  gl: WebGL2RenderingContext, tex: THREE.Texture,
): { format: number; type: number } | null {
  const format = tex.format === THREE.RedFormat ? gl.RED
    : tex.format === THREE.RGFormat ? gl.RG
      : tex.format === THREE.RGBAFormat ? gl.RGBA
        : null;
  const type = tex.type === THREE.FloatType ? gl.FLOAT
    : tex.type === THREE.HalfFloatType ? gl.HALF_FLOAT
      : tex.type === THREE.UnsignedByteType ? gl.UNSIGNED_BYTE
        : tex.type === THREE.UnsignedShortType ? gl.UNSIGNED_SHORT
          : null;
  return format !== null && type !== null ? { format, type } : null;
}

/** One band's rows as a view on the level's own array, copied nowhere. */
function bandView(
  data: UploadLevel['data'], from: number, length: number,
): ArrayBufferView {
  const off = data.byteOffset + from * data.BYTES_PER_ELEMENT;
  if (data instanceof Float32Array) return new Float32Array(data.buffer, off, length);
  if (data instanceof Uint16Array) return new Uint16Array(data.buffer, off, length);
  return new Uint8Array(data.buffer, off, length);
}

/**
 * Upload one texture in row bands across frames, not in the frame that mounts.
 *
 * `renderer.initTexture` is one `texSubImage2D` per mip level and nothing
 * divides one: the plot's height field is 23.6 MB plus its own chain and read
 * 38.9 ms on the frame the mesh mounted, which is most of what a PLOT to
 * SURFACE handoff cost. `source.dataReady` is three's own "allocate the
 * storage, upload nothing" switch, so the texture is allocated once and the
 * rows follow in bands.
 *
 * Each band is a raw `texSubImage2D` over a view of the level's own array and
 * not `renderer.copyTextureToTexture`, which reads five `UNPACK_*` parameters
 * back off the context per call to restore them afterwards. Each of those is a
 * command-buffer round trip that waits on whatever the GPU service is doing,
 * and behind a freshly allocated 23.6 MB texture that was 15 to 25 ms each: one
 * band, five queries, a 128 ms frame. Nothing here asks the context a question.
 *
 * A texture backed by an image rather than an array, or a format pair this
 * cannot name, has no bands to cut and goes up whole.
 */
export async function uploadTextureStaged(
  renderer: THREE.WebGLRenderer,
  tex: THREE.Texture,
  nextFrame: () => Promise<void>,
  alive: () => boolean,
): Promise<void> {
  const levels = uploadLevelsOf(tex);
  const gl = renderer.getContext();
  const enums = levels && gl instanceof WebGL2RenderingContext ? bandEnums(gl, tex) : null;
  if (!levels || !enums || !(gl instanceof WebGL2RenderingContext)) {
    renderer.initTexture(tex);
    return;
  }
  const ready = tex.source.dataReady;
  tex.source.dataReady = false;
  renderer.initTexture(tex);
  tex.source.dataReady = ready;
  const handle = (renderer.properties.get(tex) as { __webglTexture?: WebGLTexture })
    .__webglTexture;
  // No handle means three did not take the allocation, so the whole upload is
  // still owed and `initTexture` is the only thing that can pay it.
  if (!handle) { renderer.initTexture(tex); return; }
  for (let level = 0; level < levels.length; level++) {
    const m = levels[level]!;
    const texels = m.width * m.height;
    if (texels < 1 || m.data.length < texels) continue;
    const rowLen = m.data.length / m.height;
    const rowBytes = rowLen * m.data.BYTES_PER_ELEMENT;
    const rows = Math.max(1, Math.floor(UPLOAD_BAND_BYTES / rowBytes));
    for (let y = 0; y < m.height; y += rows) {
      const h = Math.min(rows, m.height - y);
      renderer.state.bindTexture(gl.TEXTURE_2D, handle);
      // three sets these per upload of its own, so leaving them as this band
      // needs them cannot reach another texture.
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, tex.unpackAlignment);
      gl.texSubImage2D(
        gl.TEXTURE_2D, level, 0, y, m.width, h, enums.format, enums.type,
        bandView(m.data, y * rowLen, h * rowLen),
      );
      await nextFrame();
      if (!alive()) return;
    }
  }
}

/**
 * The plot's three programs, linked before the descent reaches the plot.
 *
 * The plot's surface shader is ONE `compileShader`/`linkProgram` pair at 66 to
 * 85 ms on the calling thread, plus three's own uniform enumeration for it, and
 * staging cannot divide one link - so the whole of it used to land on the frame
 * the mesh mounted, at the SURFACE edge. This builds the same three materials
 * from the same source strings, off a degenerate triangle, so the mount finds
 * every program already in three's cache. `atmoRef` is why it cannot be done
 * once for the session: the scattering chunk is generated per body, so the
 * source - and the key - belong to the focused body.
 *
 * The materials stay alive for the session for the reason `warmTilePrograms`
 * records: three's `dispose` drops the program reference and the last one
 * destroys the linked program.
 */
export function warmPlotPrograms(
  renderer: THREE.WebGLRenderer, atmoRef: PlotAtmoRef | null, shadowRef: PlotShadowRef | null,
): { readonly group: THREE.Group; dispose(): void } {
  const defines = renderer.extensions.has('OES_texture_float_linear')
    ? {} : { MANUAL_BILINEAR: 1 };
  // Empty: a uniform's VALUE is no part of a program's cache key, and three
  // filters its own upload list by the keys this bag actually carries, so
  // nothing here is ever bound.
  const uniforms: Record<string, THREE.IUniform> = {};
  const mats = [
    plotSurfaceMaterial(uniforms, defines, atmoRef, shadowRef),
    plotWallMaterial(uniforms, defines, atmoRef, shadowRef),
    plotGroundMaterial(uniforms, atmoRef, shadowRef),
  ];
  const names = ['plot-warm-surface', 'plot-warm-wall', 'plot-warm-ground'];
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(9), 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array([0, 1, 0, 0, 1, 0, 0, 1, 0]), 3));
  geometry.setAttribute('isTop', new THREE.BufferAttribute(new Float32Array(3), 1));
  const group = new THREE.Group();
  group.name = 'plot-program-warm';
  for (let i = 0; i < mats.length; i++) {
    mats[i]!.name = names[i]!;
    const mesh = new THREE.Mesh(geometry, mats[i]!);
    mesh.frustumCulled = false;
    group.add(mesh);
  }
  return {
    group,
    dispose(): void {
      for (const m of mats) m.dispose();
      geometry.dispose();
    },
  };
}

export class DtmPlot {
  readonly group = new THREE.Group();
  readonly field: PlotField;
  readonly subPlots: SubPlot[] = [];
  /** Cells this plot surveyed for itself in `measureStaged` - 0 once the claim
   *  hands its own survey over. What `--ship`'s `plot-measure` bar reads. */
  surveyedHere = 0;
  /** Arithmetic, not wall, inside `measureStaged`'s two loops. */
  surveyWorkMs = 0;
  scoreWorkMs = 0;
  /** Boulder scatter, when the bake measured an abundance for this ground.
   *  Rebuilt as the camera moves; see updateRocks(). */
  rocks: RockField | null = null;
  private rockSeed = 0;
  /** The vehicle, once deployed. Null until someone asks for it. */
  rover: Rover | null = null;
  /** The buildings standing here, drawn as meshes over their pads; built on the first placement. */
  structures: StructureMeshes | null = null;
  private hasCsm = false;
  private rockTuning: RockTuning = ROCK_DEFAULTS;
  private readonly rockLaw: RockLaw | null;
  /** Whether the rover and the rock scatter cast onto the cascades - the
   *  SURFACE band only, applied here since either can be (re)built after the
   *  edge already flipped it. See setShadowCasting(). */
  private shadowCastingOn = false;
  /** One edge of the render lattice, in metres: `meshHeightAt`'s cell size. */
  private latticeStepM = 0;
  /** Where the lattice vertex at axial (0, 0) sits, in plot-local metres. */
  private latticeOrigin = { x: 0, z: 0 };

  /** `setClaimCircum`: the pre-fix circumcircle rasterisation, for the control. */
  private claimCircum = false;
  /** See holdRocks(). */
  private rocksHeld = false;

  private surfaceMat!: THREE.ShaderMaterial;
  private wallMat!: THREE.ShaderMaterial;
  private overlayTex!: THREE.DataTexture;
  private uniforms!: Record<string, THREE.IUniform>;
  private meshLiftM = 0;
  /** The palette's own fill terms, which `setFillTint` scales. */
  private fillBase: Pick<PlotPaletteRef, 'sky' | 'bounce' | 'wallFill'> = neutralPalette();
  private readonly fillTint = new THREE.Vector3(1, 1, 1);
  private readonly fillSkyLight = new THREE.Vector3(0, 0, 0);
  private tintRaw: readonly [number, number, number] | null = null;
  private tintLevel: number | null = null;
  /** See setTintCurve(): kept so a colour arriving later is levelled the same way. */
  private tintCurve: AlbedoRamp | null = null;
  private exag: number;
  /** The body's season, solar day and dust curve - what `measure` scores against. */
  readonly climate: PlotClimate;
  private groundMesh!: THREE.Mesh;
  private wallsMesh!: THREE.Mesh;
  /** The cluster's own extent, metres — reported, not a scale factor. */
  clusterExtentM = 1;
  /** Half-extents of the ground, metres — a claim is a long thin strip,
   *  and anything flying over it needs to know which way is long. */
  spanX = 1;
  spanZ = 1;
  /** Height of the terrain's ceiling, metres, at current exaggeration. */
  get ceiling(): number {
    return this.field.meta.elevation.relief_m * this.exag;
  }

  constructor(
    field: PlotField,
    albedo: THREE.Texture | null,
    renderer: THREE.WebGLRenderer,
    opts: PlotOptions,
  ) {
    this.field = field;
    this.climate = opts.climate;
    this.rockLaw = opts.rockLaw ?? null;
    this.tintRaw = opts.tint ?? null;
    this.tintLevel = opts.tint ? opts.tintLevel ?? null : null;
    this.exag = opts.exaggeration ?? 2.5;
    const atmoRef = opts.atmo ?? null;
    const shadowRef = opts.shadow ?? null;
    this.hasCsm = shadowRef !== null;
    const ramp = opts.albedoRamp ?? DEFAULT_ALBEDO_RAMP;
    const palette = opts.palette ?? neutralPalette();
    this.fillBase = palette;

    const R = field.meta.hex.radius_m;
    const relief = field.meta.elevation.relief_m;
    const places = field.placements;

    // The rings, the origin and the axial places, derived once so the buffers
    // the worker built and the ones the fallback builds are the same lattice.
    const spec = plotLatticeSpec(field, opts.detail);
    const detail = spec.rings;

    // Cluster centroid and extent, needed up front: the ground disc has to be
    // built at its true size in METRES, because its vertex shader offsets y in
    // metres and any mesh scale would multiply that offset too.
    let cx = 0;
    let cz = 0;
    for (const p of places) {
      cx += p.x_m;
      cz += p.z_m;
    }
    cx /= places.length;
    cz /= places.length;
    let ex = 0;
    for (const p of places) {
      ex = Math.max(ex, Math.hypot(p.x_m - cx, p.z_m - cz) + R);
    }

    const origin = spec.origin;
    this.latticeOrigin = { x: origin.x, z: origin.z };

    // ── height texture: the same Float32Array the CPU measures from ─────────
    const heightTex = new THREE.DataTexture(
      field.norm,
      field.width,
      field.texHeight,
      THREE.RedFormat,
      THREE.FloatType,
    );
    heightTex.name = 'plot-height';
    heightTex.wrapS = heightTex.wrapT = THREE.ClampToEdgeWrapping;
    heightTex.generateMipmaps = false;
    const floatLinear = renderer.extensions.has('OES_texture_float_linear');
    if (floatLinear) {
      heightTex.magFilter = THREE.LinearFilter;
      heightTex.minFilter = THREE.LinearMipmapLinearFilter;
      // GL level 0 is `mipmaps[0]` and `buildHeightMips` starts at HALF
      // resolution, so handing over the chain alone left the full-res array
      // unreachable and every height read an octave coarser than the `uLod`
      // computed below against the full-res texel size.
      heightTex.mipmaps = [
        { data: field.norm, width: field.width, height: field.texHeight },
        ...field.mips,
      ] as unknown as typeof heightTex.mipmaps;
    } else {
      heightTex.magFilter = heightTex.minFilter = THREE.NearestFilter;
    }
    heightTex.needsUpdate = true;

    // Which level the lattice can actually represent: one step of the vertex
    // grid against one texel of the field.
    const latticeStepM = R / detail;
    this.latticeStepM = latticeStepM;
    const texelM = field.meta.bbox_m.width / field.width;
    const geoLod = floatLinear ? Math.max(0, Math.log2(latticeStepM / texelM)) : 0;

    if (albedo) {
      albedo.flipY = false;
      albedo.name = 'plot-ortho';
      albedo.wrapS = albedo.wrapT = THREE.ClampToEdgeWrapping;
      albedo.colorSpace = THREE.NoColorSpace;
      albedo.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
      // One byte a texel, not four. The ortho is a single greyscale channel and
      // the shader only ever reads .r, but an image-backed texture defaults to
      // RGBA and would upload three redundant copies of it. Declaring RedFormat
      // makes three pick R8 as the internal format, which is what lets the
      // albedo go from 2059px to 4096px — 4x the texels — while OCCUPYING THE
      // SAME GPU MEMORY as before: 2059² x 4 bytes and 4096² x 1 byte are both
      // about 17 MB.
      albedo.format = THREE.RedFormat;
    }

    // Overlay matches the field's aspect so hex rims stay circular-ish rather
    // than smeared along the long axis of a region strip.
    const ovW = 512;
    const ovH = Math.max(
      64,
      Math.round((ovW * field.meta.bbox_m.height) / field.meta.bbox_m.width),
    );
    this.overlayTex = makeOverlayTexture(ovW, Math.min(4096, ovH));

    this.uniforms = {
      // The shared LUT contract (src/atlas/atmosphere.ts) merged wholesale,
      // the same way the sky quad does - the SAME uniform objects atlas.ts
      // owns, so a value it sets this frame needs touching only once.
      ...(atmoRef ? atmoRef.atmo.uniforms : {}),
      ...(atmoRef ? atmoRef.sharedUniforms : {}),
      ...(shadowRef ? shadowRef.uniforms : {}),
      uHeight: { value: heightTex },
      uAlbedo: { value: albedo },
      uOverlay: { value: this.overlayTex },
      uBBox: { value: new THREE.Vector2(field.meta.bbox_m.width, field.meta.bbox_m.height) },
      uTexel: { value: new THREE.Vector2(field.width, field.texHeight) },
      uRelief: { value: relief },
      uExag: { value: this.exag },
      uLod: { value: geoLod },
      uHexR: { value: R },
      uSunDir: { value: new THREE.Vector3(0.4, 0.5, 0.3).normalize() },
      uSunFlux: { value: 1.0 },
      // x = verdict fill, y = sub-cell lines, z = parcel boundaries. Separate,
      // because they answer different questions and want different weights:
      // the parcels are the property, the cells are the survey, and the fill is
      // a mood. At claim scale the cells become a honeycomb that buries the
      // terrain, so they are the first thing to give up.
      uGridMix: { value: new THREE.Vector3(0, 0, 0) },
      uContour: { value: 0.0 },
      uContourInterval: { value: 20.0 },
      uHasAlbedo: { value: albedo ? 1.0 : 0.0 },
      uTint: { value: inheritedTint(opts.tint ?? null) },
      uHasTint: { value: opts.tint ? 1.0 : 0.0 },
      uTintMap: { value: null },
      uHasTintMap: { value: 0.0 },
      uTintGain: { value: new THREE.Vector3(1, 1, 1) },
      uOrthoMean: { value: Math.max(1e-3, opts.orthoMean ?? 0.5) },
      uOrthoLow: { value: opts.orthoLowPass ?? null },
      uHasOrthoLow: { value: opts.orthoLowPass ? 1.0 : 0.0 },
      // How far this mesh owns its own pixels; `setFarField` writes it, and
      // with no leaves behind it there is nothing to hand the far ground to.
      uFarCutM: { value: 1e9 },
      // Scale the cut to the relief, not to the hex width. Keyed to the plot's
      // own width the walls tower over 200 m of terrain and the ground reads as
      // a lid on a box; keyed to the relief they stay a plinth under it.
      uWallDepth: {
        value: Math.max(
          relief * this.exag * (opts.wallDepthFraction ?? 0.45),
          field.meta.hex.across_corners_m * 0.02,
        ),
      },
      uReliefScale: { value: relief * this.exag },
      // The body's own radius. The plain drops away by d^2/2R, which is what
      // puts a horizon at a believable distance instead of at the disc's edge.
      uCurveR: { value: opts.curveRadiusM },
      // Selected sub-plot: xy = centre in region metres, z = radius (<0 = none).
      uSelect: { value: new THREE.Vector3(0, 0, -1) },
      // How much of that selection is drawn: 2 wash and rim, 1 rim, 0 neither.
      // The same three states `src/atlas/subPlotMark.ts` carries above the
      // SURFACE edge, because it is one control over one thing.
      uSelMode: { value: 2 },
      // Camera in the plot's OWN frame. vW is local metres and cameraPosition
      // is world units, so every shader that mixed them was computing a view
      // vector pointing at the origin instead of at the eye.
      uCamLocal: { value: new THREE.Vector3() },
      // Iron-oxide ramp endpoints the ortho image is mixed against, from the
      // body's own atmosphere block (docs/roadmap/05-atmosphere-and-light.md,
      // "What this replaces").
      uAlbedoLow: { value: new THREE.Vector3(...ramp.low) },
      uAlbedoHigh: { value: new THREE.Vector3(...ramp.high) },
      uSunCol: { value: new THREE.Vector3(...palette.sun) },
      // The palette's beam is the sun's high in the sky; `setSunTint` is what
      // the air takes out of it on a longer path.
      uSunTint: { value: new THREE.Vector3(1, 1, 1) },
      uSkyCol: { value: new THREE.Vector3(...palette.sky) },
      uBounceCol: { value: new THREE.Vector3(...palette.bounce) },
      uWallFill: { value: new THREE.Vector3(...palette.wallFill) },
      uWallEdge: { value: new THREE.Vector3(...palette.wallEdge) },
      uWallLow: { value: new THREE.Vector3(...palette.wallLow) },
      uWallHigh: { value: new THREE.Vector3(...palette.wallHigh) },
      uPlainLow: { value: new THREE.Vector3(...palette.plainLow) },
      uPlainHigh: { value: new THREE.Vector3(...palette.plainHigh) },
      // A true-scale reference footprint: xy centre, z half-width in metres
      // (<0 = off). Drawn in the shader so it conforms to the ground for free.
      // Placed structures: (x, z, halfWidth, kind). uPad is the one being
      // previewed under the cursor; uPads are the ones standing.
      uPad: { value: new THREE.Vector4(0, 0, -1, 0) },
      uPads: { value: Array.from({ length: 8 }, () => new THREE.Vector4(0, 0, -1, 0)) },
      uPadCount: { value: 0 },
      // Lattice geometry, so the shader can evaluate the grid instead of
      // reading a picture of it.
      uLatOrigin: { value: new THREE.Vector2(origin.x, origin.z) },
      uHexGrid: { value: new THREE.Vector3(R, R / 7.35, 3) },
      // Which parcels are held, as a lookup indexed by lattice coordinate.
      // A per-fragment loop over a claims list would cost more the more land
      // the player owns, which is precisely backwards.
      uClaims: { value: makeClaimTexture() },
      uTime: { value: 0 },
      // Measured rock abundance, driving the fine clast lag the same way it
      // drives the boulder scatter. Default is the Martian site median.
      uRockK: { value: rockLagK(field.meta.rocks, opts.rockLaw ?? null) },
      // Master switch for the regolith detail. ?noRegolith=1 turns it off, so
      // the two states can be captured and DIFFED rather than argued about —
      // "it looks more detailed" is not a measurement.
      uRegolith: {
        value: typeof location !== 'undefined' && TEST_SWITCHES
          ? Number(new URLSearchParams(location.search).get('regolith') ?? 1) : 1.0,
      },
      // ?bedforms=1 draws them on an airless body anyway: `descent-dojo --smear`'s control.
      uBedforms: {
        value: opts.bedforms || (typeof location !== 'undefined' && TEST_SWITCHES
          && new URLSearchParams(location.search).get('bedforms') === '1') ? 1 : 0,
      },
      // ?ripples=fall steers flat ground off the fall line as one field again, with no crossing domains.
      uRippleFall: {
        value: typeof location !== 'undefined' && TEST_SWITCHES
          && new URLSearchParams(location.search).get('ripples') === 'fall' ? 1 : 0,
      },
    };

    const defines = floatLinear ? {} : { MANUAL_BILINEAR: 1 };

    this.surfaceMat = plotSurfaceMaterial(this.uniforms, defines, atmoRef, shadowRef);

    this.wallMat = plotWallMaterial(this.uniforms, defines, atmoRef, shadowRef);

    // The lattice: the worker's buffers wrapped with no copy, or the same
    // arithmetic here when there is no worker to have built them.
    const surface = new THREE.Mesh(
      hexLattice(opts.lattice ?? buildLatticeBuffers(R, detail, spec.places, origin), R),
      this.surfaceMat,
    );
    surface.frustumCulled = false;
    surface.renderOrder = 0;
    this.group.add(surface);

    // ── the surrounding plain ────────────────────────────────────────────
    // Without this the plots hang in the void and the sun sets THROUGH the
    // ground, which reads as a missing object rather than as a stylisation.
    // A disc at the base of the cut, dropped by real Martian curvature so it
    // falls away into a horizon instead of ending at a rim.
    const groundMat = plotGroundMaterial(this.uniforms, atmoRef, shadowRef);
    // Radius in METRES — 55 cluster-extents, which lands just inside the sky
    // dome. Baked into the geometry rather than set as a mesh scale, for the
    // reason above.
    const ground = new THREE.Mesh(new THREE.CircleGeometry(55 * ex, 96), groundMat);
    ground.geometry.rotateX(-Math.PI / 2);
    ground.frustumCulled = false;
    ground.renderOrder = -1;
    this.groundMesh = ground;
    this.group.add(ground);

    const walls = new THREE.Mesh(
      hexWalls(R, Math.max(24, detail >> 2), 1, places),
      this.wallMat,
    );
    walls.frustumCulled = false;
    walls.renderOrder = 1;
    this.wallsMesh = walls;
    this.group.add(walls);

    // Boulders. Seeded from the ground's own coordinates so a plot scatters the
    // same way for everyone, every time — the address would be better still,
    // but it resolves asynchronously and the field must not wait on it.
    //
    // The field itself is built on the first update(), once there is a camera
    // to centre it on.
    this.rockSeed =
      ((Math.round(field.meta.center_lat_deg * 1e4) * 73856093) ^
        (Math.round(field.meta.center_lon_deg * 1e4) * 19349663)) >>> 0;

    // Everything above is in metres, and stays in metres: recentre on the
    // CLUSTER's centroid, not one hex — otherwise a sixteen-plot chain frames
    // like a single plot and walks off the screen — but do not scale.
    this.group.position.set(-cx, 0, -cz);
    this.clusterExtentM = ex;
    let sx = 0;
    let sz = 0;
    for (const p of places) {
      sx = Math.max(sx, Math.abs(p.x_m - cx) + (R * SQRT3) / 2);
      sz = Math.max(sz, Math.abs(p.z_m - cz) + R);
    }
    this.spanX = sx;
    this.spanZ = sz;
  }

  /**
   * The same survey, spread over frames.
   *
   * `measure` is 40 ms on a Jezero region and the SURFACE edge cannot afford it
   * in one frame. `budgetMs` is how long a slice may run before the caller's
   * `yieldFrame` gets the thread back; the cells and their verdicts are
   * `measure`'s own, since both drain the same generators.
   */
  async measureStaged(
    rings: number, lsDeg: number, yieldFrame: () => Promise<void>, budgetMs = 8,
  ): Promise<SubPlot[]> {
    let t0 = performance.now();
    let worked = 0;
    const slice = async (): Promise<void> => {
      const dt = performance.now() - t0;
      if (dt < budgetMs) return;
      worked += dt;
      await yieldFrame();
      t0 = performance.now();
    };
    this.surveyedHere = 0;
    if (this.subPlots.length === 0) {
      for (const cell of this.field.surveyCells(rings)) {
        this.subPlots.push(cell);
        this.surveyedHere++;
        await slice();
      }
    }
    // Arithmetic rather than wall: both loops yield the thread at `budgetMs`,
    // so their wall span is mostly whatever else the page drew. What the
    // handover takes out is the work, and this is the work.
    this.surveyWorkMs = worked + (performance.now() - t0);
    worked = 0;
    t0 = performance.now();
    this.setSubCellGrid(rings);
    for (const _ of this.field.scoreCells(this.subPlots, lsDeg, this.climate)) await slice();
    this.scoreWorkMs = worked + (performance.now() - t0);
    paintOverlay(this.overlayTex, this.field, this.subPlots);
    return this.subPlots;
  }

  /**
   * Take the survey the caller has already scored over this same field.
   *
   * `loadClaim` in src/atlas.ts slices `surveyCells` at the claim, so without
   * this the SURFACE build runs those 361-sample discs a second time before
   * the walkaround can hand over - the two of them sharing one frame budget
   * when the claim lands late. Called as late as the build can leave it, since
   * a claim still slicing when the mesh is constructed may well be finished by
   * the time the textures are up. Refused once this plot has surveyed anything
   * of its own; `surveyAudit` is what says the rows agree.
   */
  adoptSurvey(rows: readonly SubPlot[]): boolean {
    if (this.subPlots.length > 0 || rows.length === 0) return false;
    // The rows themselves, not copies: after the handover `plotSubs` and this
    // array are the same survey, which is what the build ended up publishing
    // anyway once `measureStaged` returned.
    this.subPlots.push(...rows);
    return true;
  }

  /**
   * The handed-over survey against one taken fresh off the same field.
   *
   * The known answer behind `PlotOptions.survey`: the rows the claim scored
   * have to be the rows this build would have computed, cell for cell. Takes
   * the whole survey in one go and is nobody's frame budget - an instrument
   * calls it once the walkaround is up.
   */
  surveyAudit(rings: number): {
    cells: number; fresh: number; missing: number;
    maxElevM: number; maxSlopeDeg: number; maxRoughM: number; maxRadiusM: number;
  } {
    const fresh = [...this.field.surveyCells(rings)];
    const mine = new Map(this.subPlots.map((p) => [p.id, p]));
    let missing = 0;
    let maxElevM = 0;
    let maxSlopeDeg = 0;
    let maxRoughM = 0;
    let maxRadiusM = 0;
    for (const f of fresh) {
      const p = mine.get(f.id);
      if (!p) { missing++; continue; }
      maxElevM = Math.max(maxElevM, Math.abs(p.elevation_m - f.elevation_m));
      maxSlopeDeg = Math.max(maxSlopeDeg, Math.abs(p.meanSlopeDeg - f.meanSlopeDeg));
      maxRoughM = Math.max(maxRoughM, Math.abs(p.roughness_m - f.roughness_m));
      maxRadiusM = Math.max(maxRadiusM, Math.abs(p.radius_m - f.radius_m));
    }
    return {
      cells: this.subPlots.length, fresh: fresh.length, missing,
      maxElevM, maxSlopeDeg, maxRoughM, maxRadiusM,
    };
  }

  /** The sub-cell lattice the shader draws, off the radii the survey used. */
  private setSubCellGrid(rings: number): void {
    const R = this.field.meta.hex.radius_m;
    (this.uniforms['uHexGrid']!.value as THREE.Vector3).set(
      R,
      this.subPlots[0]?.radius_m ?? R / (rings * 2 + 1.35),
      rings,
    );
  }

  /**
   * The textures the mesh's first draw would upload, largest first.
   *
   * `renderer.initTexture` on one of these per frame is what keeps the frame
   * the mesh mounts on off the 23.6 MB height field.
   */
  get textures(): THREE.Texture[] {
    const out: THREE.Texture[] = [];
    for (const name of ['uHeight', 'uAlbedo', 'uOverlay', 'uClaims']) {
      const t = this.uniforms[name]?.value as THREE.Texture | null | undefined;
      if (t) out.push(t);
    }
    return out;
  }

  /** Recompute sub-plot verdicts for a season, and repaint the overlay. */
  measure(rings: number, lsDeg: number): SubPlot[] {
    if (this.subPlots.length === 0) {
      this.subPlots.push(...this.field.buildSubPlots(rings));
    }
    // The shader draws the sub-cell lattice itself, so it needs the same child
    // radius buildSubPlots used — not a constant that happens to match today.
    this.setSubCellGrid(rings);
    this.field.scoreSubPlots(this.subPlots, lsDeg, this.climate);
    paintOverlay(this.overlayTex, this.field, this.subPlots);
    return this.subPlots;
  }

  /** Place the sun from altitude/azimuth in radians, plus a flux multiplier. */
  setSun(altRad: number, azRad: number, fluxRel: number): void {
    const dir = this.uniforms['uSunDir']!.value as THREE.Vector3;
    dir.set(Math.cos(altRad) * Math.sin(azRad), Math.sin(altRad), -Math.cos(altRad) * Math.cos(azRad));
    dir.normalize();
    this.uniforms['uSunFlux']!.value = fluxRel;
  }

  /** The direct beam's hue at this height of the sun over its hue overhead, per channel. */
  setSunTint(tint: THREE.Vector3): void {
    (this.uniforms['uSunTint']!.value as THREE.Vector3).copy(tint);
  }

  /**
   * The palette's sky, bounce and wall fill, scaled per channel by what the air
   * lets down to the ground, with `skyLight` added to the sky term: a deck's
   * own diffuse light, in the units the aerial in-scatter is added in.
   */
  setFillTint(tint: THREE.Vector3, skyLight?: THREE.Vector3): void {
    this.fillTint.copy(tint);
    if (skyLight) this.fillSkyLight.copy(skyLight);
    else this.fillSkyLight.set(0, 0, 0);
    const put = (k: string, c: readonly [number, number, number]): void => {
      (this.uniforms[k]!.value as THREE.Vector3).set(c[0] * tint.x, c[1] * tint.y, c[2] * tint.z);
    };
    put('uSkyCol', this.fillBase.sky);
    (this.uniforms['uSkyCol']!.value as THREE.Vector3).add(this.fillSkyLight);
    put('uBounceCol', this.fillBase.bounce);
    put('uWallFill', this.fillBase.wallFill);
  }

  /**
   * Advance the pulse clock, and put the eye into the plot's own frame.
   *
   * `eye` is in the walkaround's own frame, this group's PARENT: the drawn
   * camera sits at the scene origin once the plot is registered with the world
   * frame, so its own position no longer says where the person is standing.
   *
   * `paneHeightPx` is the pane the Atlas draws into, not the window: it is what
   * turns a rock's diameter into pixels, and the scatter's own draw floor is
   * the only thing here that needs it.
   */
  update(dt: number, eye?: THREE.Vector3 | null, camera?: THREE.Camera, paneHeightPx?: number): void {
    this.uniforms['uTime']!.value = (this.uniforms['uTime']!.value as number) + dt;
    if (eye) {
      this.group.updateMatrix();
      (this.uniforms['uCamLocal']!.value as THREE.Vector3)
        .copy(eye)
        .applyMatrix4(_invWorld.copy(this.group.matrix).invert());
      this.updateRocks(camera, paneHeightPx);
      this.structures?.update(this.uniforms['uCamLocal']!.value as THREE.Vector3, camera, paneHeightPx);
    }
  }

  /**
   * Keep the boulder field under the camera.
   *
   * The instance budget buys one rock every twelve metres if it is spread over
   * the whole plot, and one every metre or two if it is spent on a disc around
   * the viewer. Since the viewer can now walk, the disc has to follow — and
   * because the scatter is generated per hashed cell, moving it does not
   * reshuffle anything already on screen.
   *
   * The disc grows with height so that pulling up does not leave a bare ring
   * just past the old radius, and the rocks coarsen to match: from a few metres
   * up you get pebbles nearby, from a few hundred only the boulders that would
   * actually be resolvable anyway.
   */
  /**
   * Hold the boulder field off, or let it build.
   *
   * The scatter's first fill and the programs its species compile are 40 ms and
   * land on whatever frame the mesh first updates on - which is the frame the
   * mesh mounts. Held while the staged build runs, released a frame or two
   * later so the two costs are not the same frame.
   */
  holdRocks(on: boolean): void {
    this.rocksHeld = on;
  }

  /** The scatter, built on demand. Null where this body declares no rock law. */
  private ensureRocks(): RockField | null {
    if (this.rocks) return this.rocks;
    const rk = this.field.meta.rocks;
    if (!rk || !(rk.k_used > 0) || rk.k_source === 'none' || this.rockLaw === null) return null;
    const rocks = createRockField(this.field, rk, this.rockLaw, this.uniforms, this.rockSeed,
                                  this.rockTuning, (x, z) => this.meshHeightAt(x, z));
    if (!rocks) return null;
    rocks.setCastShadow(this.shadowCastingOn);
    this.group.add(rocks.group);
    this.rocks = rocks;
    // A rover deployed before the scatter existed would otherwise never learn
    // about it: `deployRover` only sees a field that is already built.
    this.rover?.setRocks(rocks);
    return rocks;
  }

  /**
   * The scatter's first fill, one band to a frame, with the hold still on.
   *
   * `holdRocks(false)` on its own hands all four unbuilt bands to the next
   * `update`, and that is one 99.8 ms frame - the PLOT to SURFACE handoff's
   * whole remaining cost once the texture uploads were banded. Called by the
   * staged build before it releases the hold, so the frames the fill takes are
   * frames nothing else of the mount is landing on.
   */
  async buildRocksStaged(
    nextFrame: () => Promise<void>, alive: () => boolean,
    centre?: { x: number; z: number },
  ): Promise<void> {
    const rocks = this.ensureRocks();
    if (!rocks) return;
    // `uCamLocal` is written by `update()`, which the caller only reaches once
    // the plot is the scene's, so a staged build that hands over on its reveal
    // frame has never run one: reading it here would spend the whole fill on
    // the bbox centre. The caller passes the ground the descent is aimed at.
    const cam = this.uniforms['uCamLocal']!.value as THREE.Vector3;
    await rocks.fillStaged(centre ?? { x: cam.x, z: cam.z }, nextFrame, alive);
    if (!alive()) return;
    this.rover?.setObstacles(rocks.obstacles);
    this.rover?.setRocks(rocks);
  }

  /**
   * The rock field's own per-frame instance reading; dojo only. `last`/`max`
   * are the staged mount fill's own; `refillLast`/`refillMax` are a walked
   * threshold's own, kept apart in `rockField.ts` itself so one path's
   * reading can never pick up instances the other one placed. `refills`
   * counts the band rebuilds a walking camera has started. `refillBand` is
   * which band the most recent one touched, and the `refillBandBase*`/
   * `refillBand*` pairs are that ONE band's own before/live counts - a
   * partial-swap check reads these instead of the four-band total, which a
   * real per-band collapse can otherwise dilute past detection.
   */
  rockFillSliceStats():
  {
    last: number; max: number; budget: number; refills: number; refillInstances: number;
    refillLast: number; refillMax: number;
    refillBand: number; refillBandBaseDrawn: number; refillBandBaseObstacles: number;
    refillBandDrawn: number; refillBandObstacles: number;
  } | null {
    return this.rocks
      ? {
        last: this.rocks.fillSliceLast, max: this.rocks.fillSliceMax,
        budget: this.rocks.fillSliceBudget, refills: this.rocks.fillSliceRefills,
        refillInstances: this.rocks.fillSliceRefillInstances,
        refillLast: this.rocks.fillSliceRefillLast, refillMax: this.rocks.fillSliceRefillMax,
        refillBand: this.rocks.fillSliceRefillBand,
        refillBandBaseDrawn: this.rocks.fillSliceRefillBandBaseDrawn,
        refillBandBaseObstacles: this.rocks.fillSliceRefillBandBaseObstacles,
        refillBandDrawn: this.rocks.fillSliceRefillBandDrawn,
        refillBandObstacles: this.rocks.fillSliceRefillBandObstacles,
      }
      : null;
  }

  /** Zero the walked-refill counters above, so a walk is read on its own. */
  rockFillSliceReset(): void {
    this.rocks?.resetFillSliceStats();
  }

  private updateRocks(camera?: THREE.Camera, paneHeightPx?: number): void {
    if (this.rocksHeld) return;
    const rocks = this.ensureRocks();
    if (!rocks) return;
    const cam = this.uniforms['uCamLocal']!.value as THREE.Vector3;
    if (rocks.update({ x: cam.x, z: cam.z })) {
      // The scatter moved, so the vehicle's idea of what is in the way is stale.
      this.rover?.setObstacles(rocks.obstacles);
    }
    // And which bands are worth drawing at all, which is the eye's HEIGHT and
    // not its ground track: the nearest rock in the disc is the one under the
    // camera, so that height is what one pixel covers at best.
    const persp = camera as THREE.PerspectiveCamera | undefined;
    if (paneHeightPx && persp?.isPerspectiveCamera) {
      // Metres of diameter one pixel covers per metre of range, and the range to
      // the nearest ground there is, which is the eye's own height over it.
      const perMetre = ROCK_MIN_PX * metresPerPixel(1, persp.fov, paneHeightPx);
      rocks.setPixelFloor(perMetre, Math.max(1, cam.y - this.meshHeightAt(cam.x, cam.z)));
    } else {
      rocks.setPixelFloor(0, 0);
    }
  }


  /**
   * Put a rover on this plot, at the centre of the cluster.
   *
   * Built on demand rather than with the plot: most visits to a plot are a
   * survey, and a survey does not need a vehicle.
   */
  deployRover(): Rover {
    if (!this.rover) {
      this.rover = new Rover(this.field, this.uniforms, this.exag,
                             (x, z) => this.meshHeightAt(x, z));
      this.rover.setCastShadow(this.shadowCastingOn);
      this.group.add(this.rover.group);
      const p = this.field.placements[0]!;
      this.rover.placeAt(p.x_m, p.z_m, 0.6);
      if (this.rocks) this.rover.setObstacles(this.rocks.obstacles);
      this.rover.setRocks(this.rocks);
    }
    return this.rover;
  }

  /**
   * Cast onto the cascades, or not - the SURFACE regime edge in src/atlas.ts.
   *
   * Applied here rather than left to the caller to repeat, because the rover
   * and the rock scatter are each built lazily (on deploy, and on the first
   * camera update) and can appear after this edge already crossed.
   */
  setShadowCasting(on: boolean): void {
    this.shadowCastingOn = on;
    this.rover?.setCastShadow(on);
    this.rocks?.setCastShadow(on);
    this.structures?.setCastShadow(on);
  }

  recallRover(): void {
    if (!this.rover) return;
    this.group.remove(this.rover.group);
    this.rover.dispose();
    this.rover = null;
  }

  /** Replace the scatter's tuning and rebuild it. */
  setRockTuning(t: RockTuning): void {
    this.rockTuning = t;
    if (this.rocks) {
      this.group.remove(this.rocks.group);
      this.rocks.dispose();
      this.rocks = null;
    }
    this.updateRocks();
  }

  /** `plotHGeo` on the CPU: the height texture at the mip `uLod` selects. */
  private plotHGeo(x: number, z: number): number {
    const f = this.field;
    const lod = this.uniforms['uLod']!.value as number;
    const scale = (this.uniforms['uRelief']!.value as number)
      * (this.uniforms['uExag']!.value as number);
    const u = Math.min(0.9995, Math.max(0.0005, x / f.meta.bbox_m.width + 0.5));
    const v = Math.min(0.9995, Math.max(0.0005, z / f.meta.bbox_m.height + 0.5));
    const l0 = Math.floor(lod);
    const ha = sampleHeightLevel(f, l0, u, v);
    const frac = lod - l0;
    if (frac <= 0) return ha * scale;
    const hb = sampleHeightLevel(f, l0 + 1, u, v);
    return (ha * (1 - frac) + hb * frac) * scale;
  }

  /**
   * The height the surface MESH DRAWS at plot-local xz, exaggerated metres.
   *
   * The one answer to "where is the drawn ground". Everything that has to
   * touch it - a rock, a wheel, the camera floor, a pick - used to read the
   * height field directly and land where the triangles never go: the lattice
   * is displaced by `plotHGeo` at the mip it can represent and then
   * interpolated linearly across a step that is metres wide.
   *
   * The lattice is analytic (see hexLattice), so the containing triangle is
   * closed form from the vertex law, and a barycentric blend of `plotHGeo` at
   * its three vertices is exactly what the rasteriser puts between them.
   *
   * Only true inside the hexagon the mesh actually covers: xz beyond it still
   * returns a number, extrapolated off the same analytic lattice, but no
   * triangle draws out there.
   */
  meshHeightAt(x: number, z: number): number {
    const a = this.latticeStepM;
    if (!(a > 0)) return this.plotHGeo(x, z);
    const o = this.latticeOrigin;
    const gr = (x - o.x) / (a * (SQRT3 / 2));
    const gq = (z - o.z) / a - gr / 2;
    const r0 = Math.floor(gr);
    const q0 = Math.floor(gq);
    const fr = gr - r0;
    const fq = gq - q0;
    // Inlined rather than closures over (r) / (q, r): this runs per sample,
    // and only two r-steps and four (q, r) vertices are ever needed.
    const halfA = a * (SQRT3 / 2);
    const vx0 = o.x + r0 * halfA;
    const vx1 = o.x + (r0 + 1) * halfA;
    const vzA = o.z + (q0 + r0 / 2) * a;             // (q0, r0)
    const vzB = o.z + (q0 + 1 + r0 / 2) * a;         // (q0 + 1, r0)
    const vzC = o.z + (q0 + 1 + (r0 + 1) / 2) * a;   // (q0 + 1, r0 + 1)
    const vzD = o.z + (q0 + (r0 + 1) / 2) * a;       // (q0, r0 + 1)
    // hexLattice winds (v0, vR, vD) then (v0, vD, vDL), so a cell splits on
    // the (q+1, r) to (q, r+1) diagonal and fq + fr says which side of it.
    if (fq + fr <= 1) {
      return this.plotHGeo(vx0, vzA) * (1 - fq - fr)
        + this.plotHGeo(vx0, vzB) * fq
        + this.plotHGeo(vx1, vzD) * fr;
    }
    return this.plotHGeo(vx0, vzB) * (1 - fr)
      + this.plotHGeo(vx1, vzC) * (fq + fr - 1)
      + this.plotHGeo(vx1, vzD) * (1 - fq);
  }

  /** Surface height in the group's local frame — what the vertex shader does. */
  private surfaceY(x: number, z: number): number {
    return this.meshHeightAt(x, z);
  }

  /**
   * March a ray against the height field and return the sub-plot it lands in.
   *
   * three's raycaster cannot help here: displacement happens in the vertex
   * shader, so the geometry it tests is a flat hexagon at y=0. Picking against
   * that is fine looking straight down and badly wrong at a grazing angle —
   * off by relief x exaggeration x cot(angle), which at the framing people
   * actually use is hundreds of metres.
   *
   * Marching the same field the shader displaces is both correct and cheap, and
   * keeps the invariant this whole module is built on: one array, every answer.
   */
  pick(rayOriginWorld: THREE.Vector3, rayDirWorld: THREE.Vector3): SubPlot | null {
    const hit = this.marchToGround(rayOriginWorld, rayDirWorld);
    return hit ? this.subPlotAt(hit.x, hit.z) : null;
  }

  /**
   * How far a ray travels before it meets the drawn ground, in metres.
   *
   * `surface-clamp --band`'s geometric half: the bottom edge of the frame
   * measured against the near plane. The group's scale is uniform, so an arc
   * length in the plot's own local frame IS metres.
   */
  rayGroundDistanceM(rayOriginWorld: THREE.Vector3, rayDirWorld: THREE.Vector3): number | null {
    return this.marchToGround(rayOriginWorld, rayDirWorld)?.t ?? null;
  }

  private marchToGround(
    rayOriginWorld: THREE.Vector3,
    rayDirWorld: THREE.Vector3,
  ): { t: number; x: number; z: number } | null {
    const inv = new THREE.Matrix4().copy(this.group.matrixWorld).invert();
    const o = rayOriginWorld.clone().applyMatrix4(inv);
    const d = rayDirWorld
      .clone()
      .transformDirection(inv)
      .normalize();
    if (Math.abs(d.y) < 1e-6 && o.y > this.field.meta.elevation.relief_m * this.exag) return null;

    const W = this.field.meta.bbox_m.width;
    const H = this.field.meta.bbox_m.height;
    const top = this.field.meta.elevation.relief_m * this.exag;

    // Enter at the top of the field's slab, so we never march the empty sky.
    let t = o.y > top && d.y < 0 ? (top - o.y) / d.y : 0;
    const step0 = Math.max(this.field.mx, this.field.mz) * 0.9;
    let step = step0;
    let prevT = t;
    let prevAbove = true;
    for (let i = 0; i < 4096; i++) {
      const px = o.x + d.x * t;
      const pz = o.z + d.z * t;
      const py = o.y + d.y * t;
      if (Math.abs(px) > W * 0.5 || Math.abs(pz) > H * 0.5) {
        if (i > 0 && t > 0) break;
      } else {
        const sy = this.surfaceY(px, pz);
        const above = py > sy;
        if (!above) {
          // Bisect between the last point above and this one below.
          let lo = prevAbove ? prevT : t - step;
          let hi = t;
          for (let k = 0; k < 24; k++) {
            const mid = (lo + hi) * 0.5;
            const mx2 = o.x + d.x * mid;
            const mz2 = o.z + d.z * mid;
            if (o.y + d.y * mid > this.surfaceY(mx2, mz2)) lo = mid;
            else hi = mid;
          }
          return { t: hi, x: o.x + d.x * hi, z: o.z + d.z * hi };
        }
        prevAbove = above;
      }
      prevT = t;
      t += step;
      step = Math.min(step * 1.02, step0 * 6);
      if (t > (W + H) * 3) break;
    }
    return null;
  }

  /** The sub-plot containing a point, in region-local metres. */
  subPlotAt(x: number, z: number): SubPlot | null {
    for (const p of this.subPlots) {
      if (insideHexZ(x - p.x, z - p.z, p.radius_m)) return p;
    }
    return null;
  }

  setSelection(p: SubPlot | null): void {
    const v = this.uniforms['uSelect']!.value as THREE.Vector3;
    if (p) v.set(p.x, p.z, p.radius_m);
    else v.set(0, 0, -1);
  }

  /** How much of the selected cell this mesh draws. */
  setCellMode(mode: SubPlotMarkMode): void {
    this.uniforms['uSelMode']!.value = mode === 'fill' ? 2 : mode === 'edge' ? 1 : 0;
  }

  /**
   * `surface-clamp --edge --inject mars-curve`: the pre-fix levelling, restored.
   *
   * The inherited colour's luminance replaced by the one a body's albedo ramp
   * gives at the orthoimage's own mean. That curve is what stood in for the
   * tone transfer these materials did not carry; the control passes Mars's own
   * row, which is where it lived. Null puts the leaves' colour back.
   */
  setTintCurve(ramp: AlbedoRamp | null): void {
    this.tintCurve = ramp;
    const raw = this.tintRaw;
    if (!raw) return;
    const u = this.uniforms['uTint']!.value as THREE.Vector3;
    const gain = this.uniforms['uTintGain']!.value as THREE.Vector3;
    gain.set(1, 1, 1);
    if (!ramp) { u.copy(inheritedTint(raw)); return; }
    const m = Math.max(0, Math.min(1, this.uniforms['uOrthoMean']!.value as number));
    const curve = ramp.low.map((c, i) => c + m * ((ramp.high[i] ?? 0) - c));
    const k = luminance(curve) / Math.max(1e-4, luminance(raw));
    u.set((raw[0] ?? 0) * k, (raw[1] ?? 0) * k, (raw[2] ?? 0) * k);
    gain.setScalar(k);
  }

  /**
   * The leaves' colour over the whole footprint (`inheritedGroundColourMap` in
   * src/atlas/groundColour.ts), in place of the one tint read at the anchor.
   * The plot owns the texture from now on; null goes back to the tint.
   */
  setColourMap(map: THREE.Texture | null): void {
    const was = this.uniforms['uTintMap']!.value as THREE.Texture | null;
    if (was && was !== map) was.dispose();
    this.uniforms['uTintMap']!.value = map;
    this.uniforms['uHasTintMap']!.value = map ? 1.0 : 0.0;
  }

  /**
   * The leaves' colour arriving after the build: what the constructor's `tint`,
   * `orthoMean` and `orthoLowPass` would have set. A low-pass handed in here is
   * the plot's from now on and goes with it at dispose.
   */
  setInheritedColour(
    tint: readonly [number, number, number], level: number,
    orthoMean: number | null, orthoLowPass: THREE.Texture | null,
  ): void {
    this.tintRaw = tint;
    this.tintLevel = level;
    this.uniforms['uHasTint']!.value = 1.0;
    if (orthoMean !== null) this.uniforms['uOrthoMean']!.value = Math.max(1e-3, orthoMean);
    if (orthoLowPass) {
      (this.uniforms['uOrthoLow']!.value as THREE.Texture | null)?.dispose();
      this.uniforms['uOrthoLow']!.value = orthoLowPass;
      this.uniforms['uHasOrthoLow']!.value = 1.0;
    }
    this.setTintCurve(this.tintCurve);
  }

  /**
   * Whether the body's own leaves are drawing the ground beyond this footprint.
   *
   * The surrounding plain and the plinth are both stand-ins for a world that
   * is not drawn: the plain so the plots do not hang in the void, the plinth so
   * the cluster's edge reads as a cut rather than as a hole. With the pyramid
   * behind them they are neither, and each would draw OVER ground of its own -
   * the plain is a flat disc 55 extents wide and the walls hang 45 per cent of
   * the relief below the edge.
   */
  setFarField(on: boolean, flatnessLimitM = Number.POSITIVE_INFINITY): void {
    if (this.groundMesh) this.groundMesh.visible = !on;
    if (this.wallsMesh) this.wallsMesh.visible = !on;
    this.uniforms['uFarCutM']!.value = on && Number.isFinite(flatnessLimitM)
      ? Math.max(1, flatnessLimitM) : 1e9;
  }

  /** Place a true-scale square footprint of `area` m2 on the selected cell. */
  /** Outline the footprint a structure WOULD occupy on this cell. */
  setReferenceFootprint(p: SubPlot | null, area: number): void {
    const v = this.uniforms['uPad']!.value as THREE.Vector4;
    if (!p || area <= 0) {
      v.set(0, 0, -1, 0);
      return;
    }
    const half = Math.sqrt(area) / 2;
    v.set(p.x, p.z, half, 0);
  }

  /**
   * Lift or drop the whole drawn mesh, metres. The handover arm's own control.
   *
   * Absolute, not cumulative, and it moves the GROUND rather than a reading of
   * it: the group's y is 0 by construction (the constructor recentres on x and
   * z only), so this is the one place a plot's ground can be put somewhere the
   * pyramid does not have it.
   */
  liftMeshM(m: number): void {
    this.meshLiftM = m;
    this.group.position.y = m;
  }

  /** How far `liftMeshM` has the drawn ground off where the field puts it. */
  get meshLift(): number { return this.meshLiftM; }

  /** Repaint with another body's palette: the handover arm's colour control. */
  setPalette(p: PlotPaletteRef): void {
    const put = (k: string, c: readonly [number, number, number]): void => {
      (this.uniforms[k]!.value as THREE.Vector3).set(c[0], c[1], c[2]);
    };
    put('uSunCol', p.sun);
    this.fillBase = p;
    this.setFillTint(this.fillTint, this.fillSkyLight);
    put('uWallEdge', p.wallEdge);
    put('uWallLow', p.wallLow);
    put('uWallHigh', p.wallHigh);
    put('uPlainLow', p.plainLow);
    put('uPlainHigh', p.plainHigh);
  }

  /** Draw with or without the orthoimage's detail; false when there is none to draw. */
  setOrthoShown(on: boolean): boolean {
    const has = this.uniforms['uAlbedo']!.value !== null;
    this.uniforms['uHasAlbedo']!.value = on && has ? 1.0 : 0.0;
    return has;
  }

  /**
   * What the surface shader paints and lights this ground with.
   *
   * Read by `surface-clamp --handover`: the plot draws the same ground the
   * leaves under it do, so the arm has to be able to see whose colour it took.
   */
  paint(): {
    hasTint: boolean; tintMap: boolean; tint: [number, number, number]; tintRaw: readonly number[] | null;
    tintLevel: number | null; orthoMean: number; orthoLowPass: boolean; hasAlbedo: boolean;
    albedoLow: [number, number, number]; albedoHigh: [number, number, number];
    sun: [number, number, number]; sky: [number, number, number];
    bounce: [number, number, number]; wallEdge: [number, number, number];
    plainHigh: [number, number, number];
  } {
    const v = (k: string): [number, number, number] => {
      const u = this.uniforms[k]!.value as THREE.Vector3;
      return [+u.x.toFixed(5), +u.y.toFixed(5), +u.z.toFixed(5)];
    };
    return {
      hasTint: (this.uniforms['uHasTint']!.value as number) > 0.5,
      tintMap: (this.uniforms['uHasTintMap']!.value as number) > 0.5,
      tint: v('uTint'),
      tintRaw: this.tintRaw,
      tintLevel: this.tintLevel,
      orthoMean: +(this.uniforms['uOrthoMean']!.value as number).toFixed(5),
      orthoLowPass: (this.uniforms['uHasOrthoLow']!.value as number) > 0.5,
      hasAlbedo: (this.uniforms['uHasAlbedo']!.value as number) > 0.5,
      albedoLow: v('uAlbedoLow'),
      albedoHigh: v('uAlbedoHigh'),
      sun: v('uSunCol'),
      sky: v('uSkyCol'),
      bounce: v('uBounceCol'),
      wallEdge: v('uWallEdge'),
      plainHigh: v('uPlainHigh'),
    };
  }

  /**
   * How far a point in the plot's own frame is above the mesh under it, exaggerated metres.
   *
   * The frame is this group's PARENT, which is the walkaround rig's: y-up
   * metres about the cluster centroid, whatever the group itself is placed at
   * in the world. A drawn-frame point goes through `frameFromWorld` first.
   *
   * The same `meshHeightAt` the floor is built on, so a point sitting exactly
   * on the floor reads exactly the floor's margin. While the floor took a MAX
   * over a lattice step and this took level 0 of the height field, the two
   * disagreed by a metre or two on rough ground and neither was the mesh.
   */
  clearanceAboveGround(frame: THREE.Vector3): number {
    this.group.updateMatrix();
    _invWorld.copy(this.group.matrix).invert();
    const local = _liftV.copy(frame).applyMatrix4(_invWorld);
    return local.y - this.surfaceY(local.x, local.z);
  }

  /** A point in the DRAWN frame into the plot's own, for a ray or a camera. */
  frameFromWorld(world: THREE.Vector3): THREE.Vector3 {
    const parent = this.group.parent;
    if (parent) {
      parent.updateWorldMatrix(true, false);
      world.applyMatrix4(_invWorld.copy(parent.matrixWorld).invert());
    }
    return world;
  }

  /**
   * Keep a point in the plot's own frame above the ground, and say whether it moved.
   *
   * Flying under the terrain is not a view of anything — the surface is
   * single-sided, so from below the world is a hole with a sky in it. Rather
   * than forbid the camera from descending, which would make close inspection
   * fight the controls, it is simply floored.
   *
   * Floored on `meshHeightAt`, the same drawn ground the rover and the rocks
   * stand on. This used to take a MAX over a lattice step of the height field,
   * to keep the eye clear of a ridge crest the mesh drew and the field's own
   * level 0 sat below; the sampler draws that crest itself.
   */
  liftAboveGround(frame: THREE.Vector3, marginM: number): boolean {
    this.group.updateMatrix();
    _invWorld.copy(this.group.matrix).invert();
    const local = _liftV.copy(frame).applyMatrix4(_invWorld);
    const floor = this.meshHeightAt(local.x, local.z) + marginM;
    if (local.y >= floor) return false;
    local.y = floor;
    frame.copy(local).applyMatrix4(this.group.matrix);
    return true;
  }

  /**
   * `liftAboveGround`'s bidirectional twin: pins the point to the ground
   * exactly, not just above it. Only a WASD footstep calls this - the
   * per-frame pivot and camera floors stay up-only, since R/F and mesh
   * refinement under a still pivot must never drop the eye.
   */
  standOnGround(frame: THREE.Vector3, marginM: number): boolean {
    this.group.updateMatrix();
    _invWorld.copy(this.group.matrix).invert();
    const local = _liftV.copy(frame).applyMatrix4(_invWorld);
    const floor = this.meshHeightAt(local.x, local.z) + marginM;
    if (local.y === floor) return false;
    local.y = floor;
    frame.copy(local).applyMatrix4(this.group.matrix);
    return true;
  }

  /**
   * The same floor taken against the DATUM sphere: the walkaround's control.
   *
   * Local y is `(elevation - min_m) * exag`, so the datum itself is at
   * `-min_m * exag` and a point floored here ignores every metre of shape the
   * mesh draws. On a body whose ground departs its datum by kilometres this is
   * the camera inside the rock, which is what the reading has to catch.
   */
  liftAboveDatum(frame: THREE.Vector3, marginM: number): boolean {
    this.group.updateMatrix();
    _invWorld.copy(this.group.matrix).invert();
    const local = _liftV.copy(frame).applyMatrix4(_invWorld);
    const floor = -this.field.meta.elevation.min_m * this.exag + marginM;
    if (local.y >= floor) return false;
    local.y = floor;
    frame.copy(local).applyMatrix4(this.group.matrix);
    return true;
  }

  /**
   * Slide a point in the plot's own frame out of any boulder it has walked into.
   *
   * `liftAboveGround`'s horizontal twin, and used the same way: the terrain
   * floors the point from below, this moves it off the rocks standing on it.
   * The scatter's own lookup does the work (`RockField.pushOut`); nothing here
   * knows where a rock is.
   */
  pushOutOfRocks(frame: THREE.Vector3, bodyR: number): boolean {
    if (!this.rocks) return false;
    this.group.updateMatrix();
    _invWorld.copy(this.group.matrix).invert();
    const local = _liftV.copy(frame).applyMatrix4(_invWorld);
    if (!this.rocks.pushOut(local.x, local.z, local.y, bodyR, _push)) return false;
    local.x = _push.x;
    local.z = _push.z;
    frame.copy(local).applyMatrix4(this.group.matrix);
    return true;
  }

  /**
   * The plot's uniform bag - sun, sky, bounce, eye, ground tint and, when the
   * plot has them, the cascades - for a mesh that stands on this ground and
   * must be lit as it is: the building viewer's (`buildingMaterial.ts`).
   */
  shaderUniforms(): Record<string, THREE.IUniform> {
    return this.uniforms;
  }

  /**
   * Structures actually standing on this plot, in plot-local metres: their
   * pads into the surface shader, and the same list, cut to the pads the
   * shader holds, to the meshes that stand on them.
   */
  setStructures(list: readonly StructureEntry[]): void {
    const pads = this.uniforms['uPads']!.value as THREE.Vector4[];
    const n = Math.min(list.length, pads.length);
    for (let i = 0; i < pads.length; i++) {
      const p = i < n ? list[i]! : null;
      if (p) pads[i]!.set(p.x, p.z, p.halfM, p.classId ?? 0.0);
      else pads[i]!.set(0, 0, -1, 0);
    }
    this.uniforms['uPadCount']!.value = n;
    if (structureMeshesOff()) return;
    if (!this.structures && n > 0) {
      this.structures = createStructureMeshes({
        uniforms: this.uniforms,
        heightAt: (x, z) => this.meshHeightAt(x, z),
        csmGlsl: this.hasCsm ? PLOT_CSM_GLSL : null,
      });
      this.structures.setCastShadow(this.shadowCastingOn);
      this.group.add(this.structures.group);
    }
    this.structures?.set(list.slice(0, n));
  }

  /** What the crafting state says about each structure, keyed `cell:kind`. */
  setStructureLooks(looks: ReadonlyMap<string, StructureLook>): void {
    this.structures?.setLooks(looks);
  }

  /** The haul runs the crafting state implies, and the site pile any of them uses. */
  setStructureHauls(routes: readonly HaulRoute[], pile: HaulPoint | null): void {
    this.structures?.setHauls(routes, pile);
  }

  /** Grid weights: verdict fill, sub-cell lines, parcel boundaries. */
  /** Mark which lattice cells are held. */
  /**
   * Mark which lattice cells are held, and which can be descended into.
   *
   * Both ride in the same byte, because the texture was already there and a
   * second one would be 4 KB to say one bit. 255 = held, 96 = has a detail
   * tile, 0 = neither; the shader thresholds rather than compares.
   *
   * Enterability needs to be VISIBLE on the ground. Only 17 of Jezero's 36
   * plots have finer data to arrive at, and with nothing to distinguish them
   * the only way to find one was to click a plot and read the panel — a coin
   * flip, repeated.
   */
  setClaimed(cells: Array<{ q: number; r: number }>,
             enterable: Array<{ q: number; r: number }> = []): void {
    const tex = this.uniforms['uClaims']!.value as THREE.DataTexture;
    const data = tex.image.data as Uint8Array;
    data.fill(0);
    const circum = this.claimCircum;
    const R = this.field.meta.hex.radius_m;
    const bboxW = this.field.meta.bbox_m.width;
    const bboxH = this.field.meta.bbox_m.height;
    const origin = this.latticeOrigin;
    // Every texel whose ground point falls inside the cell's own HEXAGON,
    // addressed in the SAME bbox-relative uv `plotUv`/vUv reads (see the
    // shader above) rather than (q, r) plus a fixed offset: a cell may cover
    // several texels or a fraction of one depending on the region's own
    // size, and an offset grid clips altogether past 32 plots from one bake
    // origin. The hexagon and not its circumcircle, or a held plot washes 13%
    // of its own radius into the six around it. A held cell is written after
    // an enterable one, so it wins where the two disagree.
    const halfW = (R * SQRT3) / 2;
    const spanX = circum ? R : halfW;
    const put = (c: { q: number; r: number }, v: number): void => {
      const cx = origin.x + R * SQRT3 * (c.q + c.r / 2);
      const cz = origin.z + 1.5 * R * c.r;
      const i0 = Math.max(0, Math.floor(((cx - spanX) / bboxW + 0.5) * 64));
      const i1 = Math.min(63, Math.ceil(((cx + spanX) / bboxW + 0.5) * 64));
      const j0 = Math.max(0, Math.floor(((cz - R) / bboxH + 0.5) * 64));
      const j1 = Math.min(63, Math.ceil(((cz + R) / bboxH + 0.5) * 64));
      for (let j = j0; j <= j1; j++) {
        const dz = Math.abs(((j + 0.5) / 64 - 0.5) * bboxH - cz) / R;
        if (dz > 1) continue;
        for (let i = i0; i <= i1; i++) {
          const dx = Math.abs(((i + 0.5) / 64 - 0.5) * bboxW - cx) / halfW;
          // A pointy-top hexagon: |x| under its flat, |z| under its vertex,
          // and the slanted edge between them. `circum` is the control's own
          // disc of the CIRCUMradius in both axes, which is what this used to
          // rasterise.
          const inside = circum
            ? ((dx * SQRT3) / 2) ** 2 + dz ** 2 <= 1
            : dx <= 1 && dz + dx * 0.5 <= 1;
          if (inside) data[j * 64 + i] = v;
        }
      }
    };
    for (const c of enterable) put(c, 96);
    // Held wins, so it is written second.
    for (const c of cells) put(c, 255);
    tex.needsUpdate = true;
  }

  /**
   * `descent-dojo --hex --claims --inject claim-circum`: the pre-fix raster.
   *
   * A held plot rasterised to its circumcircle washes 13 per cent of its own
   * radius into each of the six around it, which is what the arm's footprint
   * bar has to be able to tell from the hexagon.
   */
  setClaimCircum(on: boolean): void {
    this.claimCircum = on;
  }

  /** Where the lattice's (0, 0) plot centre sits in region metres. */
  latticeOriginM(): { x: number; z: number } {
    return { x: this.latticeOrigin.x, z: this.latticeOrigin.z };
  }

  /** The claim texture's own bytes, for the arm's footprint reading. */
  claimTexels(): Uint8Array {
    const tex = this.uniforms['uClaims']!.value as THREE.DataTexture;
    return tex.image.data as Uint8Array;
  }

  setOverlay(fill: number, cells = fill, parcels = fill): void {
    (this.uniforms['uGridMix']!.value as THREE.Vector3).set(fill, cells, parcels);
  }

  setContours(on: number, intervalM = 20): void {
    this.uniforms['uContour']!.value = on;
    this.uniforms['uContourInterval']!.value = intervalM;
  }

  setExaggeration(x: number): void {
    this.exag = x;
    this.rover?.setExaggeration(x);
    this.uniforms['uExag']!.value = x;
  }

  get exaggeration(): number {
    return this.exag;
  }

  dispose(): void {
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
      }
    });
    this.overlayTex.dispose();
    (this.uniforms['uHeight']!.value as THREE.DataTexture).dispose();
    // Material.dispose() does NOT release the textures a material samples, and
    // the ortho is the biggest single thing a plot owns — 2059x2059 of it once
    // the tiles went source-exact. Nothing else references it, because each
    // plot loads its own.
    const ortho = this.uniforms['uAlbedo']?.value as THREE.Texture | null;
    // The decode's own bitmap, which a texture dispose does not release.
    if (ortho?.image instanceof ImageBitmap) ortho.image.close();
    ortho?.dispose();
    (this.uniforms['uOrthoLow']?.value as THREE.Texture | null)?.dispose();
    (this.uniforms['uTintMap']?.value as THREE.Texture | null)?.dispose();
    (this.uniforms['uClaims']?.value as THREE.DataTexture | null)?.dispose();
    // The traverse above disposes the InstancedMesh's geometry and material as
    // Mesh subclasses, but not the instance buffers three allocates for it.
    this.rocks?.dispose();
    this.rover?.dispose();
    this.structures?.dispose();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Flyover
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Camera pose for a cinematic pass over a plot, at normalised time u.
 *
 * A lemniscate — sin(t) along the long axis against sin(2t) across it — gives a
 * figure eight that crosses its own centre, so the same ground is seen twice
 * from opposite headings. That is what sells a shadow as a shadow rather than
 * as a texture: you watch it fall the other way.
 *
 * Lives here rather than in the Atlas so the preview page can fly the identical
 * path, which is the only way to judge the framing without a human watching.
 */
export function flyoverFrame(
  u: number,
  plot: DtmPlot,
  outPos: THREE.Vector3,
  outLook: THREE.Vector3,
): void {
  // Arrival: high and wide, easing down and tightening in over the first
  // quarter. Cubic rather than smoothstep, so the drop keeps some of its speed
  // instead of settling early.
  const a = Math.min(1, u / 0.26);
  const ease = 1 - Math.pow(1 - a, 3);

  const LAPS = 2.25;
  const AHEAD = 0.34;
  const th = u * LAPS * Math.PI * 2;

  // 0.18, 2.7 and 0.13 were plot units; `extM` puts them back in the metre
  // frame `spanX`/`spanZ`/`ceiling` already carry.
  const extM = plot.clusterExtentM;
  const sx = Math.max(0.18 * extM, plot.spanX);
  const sz = Math.max(0.18 * extM, plot.spanZ);
  // Amplitudes stay INSIDE the footprint. The first cut used the full span
  // times 1.35, which swung the camera off the end of the claim on every
  // crossing and pointed it at empty ground.
  const ax = sx * 0.62;
  const az = sz * 0.70;
  const at = (t: number, spread: number, out: THREE.Vector3): void => {
    out.set(Math.sin(2 * t) * ax * spread, 0, Math.sin(t) * az * spread);
  };

  const ceil = plot.ceiling;
  at(th, 1.0 + (1.0 - 1.0) * ease, outPos);
  outPos.multiplyScalar(1 + 0.9 * (1 - ease)); // start wider, tighten in
  outPos.y = 2.7 * extM + (ceil * 3.0 + 0.13 * extM - 2.7 * extM) * ease;

  // At altitude, look at the claim itself — a camera aimed straight down at its
  // own ground track frames a patch of dirt and leaves the subject off-screen.
  // Only once we are down among it does looking AHEAD start to read as flight.
  at(th + AHEAD, 1.0, outLook);
  outLook.y = ceil * 0.30;
  outLook.multiplyScalar(ease);
}

// ─────────────────────────────────────────────────────────────────────────────
// Buildability overlay
// ─────────────────────────────────────────────────────────────────────────────

/** Parcel boundary — bone, not a verdict colour. Ownership is not a judgement. */
const PARCEL_LINE: [number, number, number] = [0.94, 0.88, 0.78];

export const VERDICT_COLOR: Record<SubPlot['verdict'], [number, number, number]> = {
  prime: [0.36, 0.95, 0.62],
  workable: [0.44, 0.80, 0.98],
  marginal: [1.0, 0.71, 0.33],
  unbuildable: [0.95, 0.34, 0.31],
};

/**
 * 64x64 R8 indexed by lattice (q, r) offset by 32 — covers any cluster we cut,
 * and turns "is this parcel held" into one texel read.
 */
function makeClaimTexture(): THREE.DataTexture {
  const t = new THREE.DataTexture(new Uint8Array(64 * 64), 64, 64, THREE.RedFormat);
  t.name = 'plot-claims';
  t.magFilter = t.minFilter = THREE.NearestFilter;
  t.generateMipmaps = false;
  t.needsUpdate = true;
  return t;
}

function makeOverlayTexture(w: number, h: number): THREE.DataTexture {
  const t = new THREE.DataTexture(new Uint8Array(w * h * 4), w, h, THREE.RGBAFormat);
  t.name = 'plot-overlay';
  t.magFilter = t.minFilter = THREE.LinearFilter;
  t.generateMipmaps = false;
  t.needsUpdate = true;
  return t;
}

/**
 * Rasterise the sub-plot verdicts into a texture the terrain shader multiplies
 * in. Painting it as an overlay rather than as floating geometry means it sits
 * exactly on the ground with no z-fighting and no second displacement path.
 */
function paintOverlay(tex: THREE.DataTexture, field: PlotField, plots: SubPlot[]): void {
  const TW = tex.image.width;
  const TH = tex.image.height;
  const data = tex.image.data as Uint8Array;
  data.fill(0);
  const W = field.meta.bbox_m.width;
  const H = field.meta.bbox_m.height;

  const stamp = (
    cxm: number,
    czm: number,
    r: number,
    col: [number, number, number],
    rimWidth: number,
    interior: number,
    rimAlpha: number,
  ): void => {
    const x0 = Math.max(0, Math.floor(((cxm - r) / W + 0.5) * TW));
    const x1 = Math.min(TW - 1, Math.ceil(((cxm + r) / W + 0.5) * TW));
    const z0 = Math.max(0, Math.floor(((czm - r) / H + 0.5) * TH));
    const z1 = Math.min(TH - 1, Math.ceil(((czm + r) / H + 0.5) * TH));
    for (let j = z0; j <= z1; j++) {
      const wz = ((j + 0.5) / TH - 0.5) * H - czm;
      for (let i = x0; i <= x1; i++) {
        const wx = ((i + 0.5) / TW - 0.5) * W - cxm;
        // Same orientation as the parent — pointy along Z. Anything else and
        // the sub-grid reads as 30 degrees out of true against the plot it is
        // drawn on, because a hexagon's symmetry folds 90 down to 30.
        if (!insideHexZ(wx, wz, r)) continue;
        const edge = hexEdgeDepth(wx, wz, r);
        const rim = 1 - Math.min(1, edge / rimWidth);
        const a = interior + (rimAlpha - interior) * rim;
        if (a <= 0.002) continue;
        const o = (j * TW + i) * 4;
        // Later stamps win where they are stronger, so a property line drawn
        // over a sub-plot fill stays visible.
        if (Math.round(a * 255) < data[o + 3]!) continue;
        data[o] = Math.round(col[0] * 255);
        data[o + 1] = Math.round(col[1] * 255);
        data[o + 2] = Math.round(col[2] * 255);
        data[o + 3] = Math.round(Math.min(1, a) * 255);
      }
    }
  };

  // Verdict fill, modulated by data tier (the Fog of Measurement).
  //
  //   tier 2 (surveyed): solid full fill — same as the old behaviour
  //   tier 1 (stereo):   solid fill at ~60% opacity (dimmed)
  //   tier 0 (orbit):    hatched fill at reduced opacity
  //
  // The hatching for tier 0 is a diagonal stripe: every other texel row is
  // transparent, producing a cross-hatch effect at the overlay's resolution.
  for (const p of plots) {
    const band = bandFor(p);
    const verdictCol = VERDICT_COLOR[band.verdictBand];

    if (p.dataTier === 2) {
      // Full solid fill — the cell is fully known.
      stamp(p.x, p.z, p.radius_m, verdictCol, 0.16, 0.30, 0.46);
    } else if (p.dataTier === 1) {
      // Dimmed solid fill — narrow band.
      stamp(p.x, p.z, p.radius_m, verdictCol, 0.16, 0.18, 0.32);
    } else {
      // Hatched fill — wide band, orbit-only data.
      // We stamp at reduced opacity and then punch out alternating rows
      // for the hatch effect.
      stamp(p.x, p.z, p.radius_m, verdictCol, 0.16, 0.14, 0.28);
    }
  }

  // Apply the hatch pattern to tier-0 cells: punch diagonal stripes into the
  // overlay by zeroing the alpha on a regular pattern. This runs AFTER all
  // stamps so the hatch is applied globally.
  const tier0Cells = plots.filter((p) => p.dataTier === 0);
  if (tier0Cells.length > 0) {
    for (const p of tier0Cells) {
      const x0 = Math.max(0, Math.floor(((p.x - p.radius_m) / W + 0.5) * TW));
      const x1 = Math.min(TW - 1, Math.ceil(((p.x + p.radius_m) / W + 0.5) * TW));
      const z0 = Math.max(0, Math.floor(((p.z - p.radius_m) / H + 0.5) * TH));
      const z1 = Math.min(TH - 1, Math.ceil(((p.z + p.radius_m) / H + 0.5) * TH));
      for (let j = z0; j <= z1; j++) {
        for (let i = x0; i <= x1; i++) {
          const wx = ((i + 0.5) / TW - 0.5) * W - p.x;
          const wz = ((j + 0.5) / TH - 0.5) * H - p.z;
          if (!insideHexZ(wx, wz, p.radius_m)) continue;
          // Diagonal hatch: every other diagonal line is transparent.
          if ((i + j) % 3 !== 0) {
            const o = (j * TW + i) * 4;
            data[o + 3] = Math.round((data[o + 3] ?? 0) * 0.25);
          }
        }
      }
    }
  }

  tex.needsUpdate = true;
}
